import {
  type Collection,
  List as ImmutableList,
  Map as ImmutableMap,
  Record as ImRecord,
  isImmutable,
} from 'immutable';
import { DateTime } from 'luxon';
import numeral from 'numeral';
import { createSelector, weakMapMemoize } from 'reselect';
import { v4 as uuid } from 'uuid';

import { accountsSelectors, type accountsTypes, accountsUtils } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import type { Category } from 'app-components/flux/categories/categoriesTypes';
import {
  chartOfAccountsSelectors,
  type chartOfAccountsTypes,
  chartOfAccountsUtils,
} from 'app-components/flux/chart-of-accounts';
import type { ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { getId } from 'app-components/flux/core/resourceStoreTypes';
import { customersSelectors } from 'app-components/flux/customers';
import type { FEATURE_FLAGS_REDUCER_KEY } from 'app-components/flux/feature-flags';
import type { filtersTypes } from 'app-components/flux/filters';
// biome-ignore lint/suspicious/noImportCycles: SIMPL-29722
import { getProjectNameById, getProjects } from 'app-components/flux/projects/projectsSelectors';
import { securitiesSelectors } from 'app-components/flux/securities';
import type { SecurityProps } from 'app-components/flux/securities/securitiesTypes';
import { tagsSelectors, tagsTypes } from 'app-components/flux/tags';
import { reduce } from 'app-components/utils/core/immutableHelpers';
import { registerMemoizedCache } from 'app-components/utils/core/reselect/selectorCacheRegistry';
import { numberUtils } from 'app-components/utils/number';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';
import { numericCollator, strCompare } from 'app-components/utils/strings';

import type { ACCOUNTS_REDUCER_KEY } from '../accounts/accountsTypes';
import type { BillableInvoiceItemType } from '../billable-invoice-items/billableInvoiceItemsTypes';
import type { CustomerAndProjects } from '../filters/filtersTypes';
import { isSplitTxn, isTransferTxn } from './transactionsPredicates';
import {
  type Allocation,
  type AllocationProps,
  InvestmentTxnType,
  type InvoiceTransaction,
  mkCashFlowTransaction,
  mkInvoiceTransaction,
  mkSplitItem,
  mkTransactionItem,
  type SplitItem,
  type SplitItemProps,
  type SplitProps,
  type TransactionItem,
  type TransactionItemCombinedProps,
  type TransactionProps,
  type TransactionRecord,
  TransactionSubType,
  type TxnPaymentMethodType,
} from './transactionsTypes';

export {
  hasExplicitSplitExclusionOverride,
  isSplitTxn,
  isTransferOrCreditCardPaymentTxn,
  isTransferTxn,
  isUITransferTxn,
} from './transactionsPredicates';

export const MAX_SUPPORTED_TRANSACTION_AMOUNT = 1_000_000_000;
export const UNSUPPORTED_TRANSACTION_AMOUNT_HELPER_TEXT = 'Please use values under a billion.';

export const isSupportedTransactionAmount = (amount: number): boolean =>
  Number.isFinite(amount) && Math.abs(amount) < MAX_SUPPORTED_TRANSACTION_AMOUNT;

/**
 * Some split items (especially for newly-created or server-normalized splits) may not have a stable `id` or `clientId`.
 * Consumers that flatten split items into maps need a deterministic key per row.
 *
 * This helper guarantees a non-empty key, preferring splitItem.id -> splitItem.clientId, and falling back to a deterministic
 * `${txnId}-split-idx-${index}` style key when needed.
 */
export const getStableSplitItemId = (
  txn: Pick<TransactionProps, 'id' | 'clientId'>,
  splitItem: Pick<SplitItemProps, 'id' | 'clientId'>,
  index?: number,
): string => {
  const direct = splitItem?.id || splitItem?.clientId;
  if (direct) {
    return String(direct);
  }

  const txnId = txn?.id || txn?.clientId;
  const prefix = txnId ? String(txnId) : 'txn';
  const suffix = typeof index === 'number' ? `idx-${index}` : 'idx-0';

  return `${prefix}-split-${suffix}`;
};

export const isInvoiceTxn = (txn: TransactionRecord) => txn.type === 'INVOICE' && txn.subtype === 'INVOICE';

// SIMPL-31531: invoices, credit memos, and sales receipts are all edited through their own form,
// so register grid cells for these should never be inline-editable, even for their split lines.
// Narrower than isInvoiceTxn on purpose - only used to gate inline grid editability, not the
// broader invoice-only behaviors (AR totals, tax calcs, icon/column rendering, etc.) that key off
// isInvoiceTxn elsewhere.
export const isInvoiceFormOnlyTxn = (txn: TransactionRecord) =>
  txn.type === 'INVOICE' &&
  (txn.subtype === TransactionSubType.Invoice ||
    txn.subtype === TransactionSubType.InvoiceCreditMemo ||
    txn.subtype === TransactionSubType.SalesReceipt);

const BUSINESS_INVOICE_SUBTYPES = new Set<string>([
  TransactionSubType.Estimate,
  TransactionSubType.Invoice,
  TransactionSubType.InvoiceCreditMemo,
  TransactionSubType.InvoicePayment,
  TransactionSubType.SalesReceipt,
  TransactionSubType.SalesRefund,
]);

/** Returns true for any transaction that belongs to the invoicing/sales workflow and should not appear in personal review flows. */
export const isInvoiceRelatedTransaction = (txn: { type?: string | null; subtype?: string | null }): boolean =>
  txn.type === 'INVOICE' ||
  txn.type === 'PAYMENT' ||
  (txn.subtype != null && BUSINESS_INVOICE_SUBTYPES.has(txn.subtype));

export const isTransactionItemReportableIncomeUtil = (
  txn: TransactionItemCombinedProps,
  categoriesById: ImmutableMap<string, Category>,
) => {
  if (!txn.coa) {
    return false;
  }
  const category = categoriesById.get(txn.coa.id);
  return category && category.type === 'INCOME';
};

// TODO -------------------------------------------------------------------
// TODO UNIT TESTS!!!
// The core function that all transfer logic depends on, it uses fuzzy logic
// to find the other side of the of a transfer transaction
//
// findMatchingTransferTx - find the matching transfer for <txn> in the list of transactions <txnList>.
//                          match amount is txn.amount, or 'altAmount' if provided
//
// Parms:
//        txn: QCS transaction record
//        txnList: list of QCS transactions from the account on the "other side" of the transfer.  This
//                 list should be pre-filtered to only contain transfer transactions.
//                 e.g. txnList = accountTransactions.filter((x) => isTransferTxn(x));
//        altAmount: override of txn.amount if desired. This is typically used when isSplitTxn(txn) and
//                   we are searching for one the other side of a split item.  In this case we pass in
//                   the split.item[index].amount rather than using txn.amount
//
// The Algorithm:
//
// For each transaction in txnList, we determine if it is a "candidate" for a possible match.  This
// is determined by 4 facets.  Amount (must be exact negative match), Date (distance between dates)
// Payee (exact string match), and coa (coa must be type ACCOUNT, and id must match txn.accountId)
//
// A candidate will exactly match Coa and Amount.  Note, the 4 facets apply individually to split lines
// if the transaction being examined is split.
//
// Once all candidates are determined, they are sorted first by the date delta (exact date matches sort
// to the top), and using payee match as a secondary sort. We then simply return the top sorted item.
//
// IMPROVEMENTS:
//
// This matching will succeed in 99% of the cases.  Error cases can be made to occur by creating transfers
// on the same day, for the same amount, with the same payee name.  We are accepting of imperfection in
// this case since the "error" is virtually meaningless to the user.  In fact, from an accounting perspective,
// there is no error.  If the user placed different tags, memo, or attachments to these transactions, then
// there may be a situation where a mismatch in this extremely rare case causes an issue.  We are NOT solving
// for this very extreme corner case.
//
// However, it would be easy to add the following improvements:
// a) add memo and tags string values as tertiary sorts
// b) use string-similarity (Dice's Coefficient, available in open source libraries) for payee match
//    for slight differences that may have been created.
// c) use 'number of attachments' as a facet as well
//
// It is hard to envision real life use cases where these corner cases matter (we have MUCH bigger fish
// to fry for our users in more core cases in all our clients), but these are ways we could address this
// if desired.
//
//
export function findMatchingTransferTx(
  txn: Partial<TransactionProps>,
  txnList: ImmutableMap<string, TransactionRecord>,
  altAmount: number | null = null,
  altTransfer: { id?: string | null; clientId?: string | null } | null = null,
): null | { txn: TransactionRecord; splitItem: number | null } {
  // we look through txnList for a transaction that matches the given transaction.
  // best indicator is date and amount, and of course, the coa
  //

  if (!txnList) {
    return null;
  }

  const srcDate = DateTime.fromISO(txn.postedOn ?? '');
  const txAmount = altAmount || txn.amount;
  const txTransfer = altTransfer || txn.transfer;

  // Split Txn
  //   Items (coa.id)

  // quick lookup if the 'transfer' field has valid ID/clientId
  let maybeTxn;
  if (txTransfer?.id) {
    maybeTxn = txnList.get(txTransfer.id);
  } else if (txTransfer?.clientId) {
    maybeTxn = txnList.find((tx) => tx.clientId === txTransfer.clientId);
  }
  if (maybeTxn) {
    if (!isSplitTxn(maybeTxn)) {
      // check that transfer matches
      let transferMatches;
      if (txn?.id) {
        transferMatches = maybeTxn.transfer?.id === txn.id;
      } else if (txn?.clientId) {
        transferMatches = maybeTxn.transfer?.clientId === txn.clientId;
      }
      // verify still connected via COA and amount (COA should always be same since user is passing in the txnList)
      if (transferMatches && maybeTxn.coa?.id === txn.accountId && Number(maybeTxn.amount) === -Number(txAmount)) {
        return { txn: maybeTxn, splitItem: null };
      }
    } else {
      // find matching split-item
      const items = maybeTxn.split?.items ?? [];
      for (let i = 0; i < (isImmutable(items) ? items.size : items.length); i++) {
        const splitItem = isImmutable(items) ? items.get(i) : items[i];
        if (
          splitItem?.transfer?.id === txn.id &&
          splitItem?.coa?.id === txn.accountId &&
          Number(splitItem?.amount) === -Number(txAmount)
        ) {
          return { txn: maybeTxn, splitItem: i };
        }
      }
    }
  }

  // didn't find via transfer link so falling back to sorting and weighting all txns in the bucket
  let txnCandidates: Array<{
    dateFacet: number;
    payeeFacet: boolean;
    txn: TransactionRecord;
    splitItem: number | null;
  }> = [];
  txnList.forEach((listTxn) => {
    // coa's must match
    // amount must match
    // payee & date match helps

    const dateFacet = srcDate.diff(DateTime.fromISO(listTxn.postedOn), 'days').as('days');
    const payeeFacet = listTxn.payee === txn.payee;

    if (isSplitTxn(listTxn)) {
      // Note: Since we are ony using date and payee to sort and find the best match, the first match canidate
      //       will be what is returned (for splits, txn-date and txb-payee are the same for all split children).
      //
      listTxn.split?.items?.forEach((splitItem, index) => {
        if (splitItem.coa?.id === txn.accountId && Number(splitItem.amount) === -Number(txAmount)) {
          txnCandidates.push({ dateFacet, payeeFacet, txn: listTxn, splitItem: index });
        }
      });
    } else {
      // check connection via COA and amount (COA should always be same since user is passing in the txnList)
      if (listTxn.coa?.id === txn.accountId && Number(listTxn.amount) === -Number(txAmount)) {
        txnCandidates.push({ dateFacet, payeeFacet, txn: listTxn, splitItem: null });
      }
    }
  });

  if (txnCandidates.length > 1) {
    // sort the list, with the best match coming to the top
    txnCandidates = txnCandidates.sort((a, b) => {
      // payee match is first priority
      if (a.payeeFacet !== b.payeeFacet) {
        return a.payeeFacet ? -1 : 1;
      }
      // next sort by date delta/difference
      if (a.dateFacet !== b.dateFacet) {
        return Math.abs(a.dateFacet) - Math.abs(b.dateFacet);
      }
      return 0;
    });
  }

  // TODO fix this bug if txCanddidates is empty
  return txnCandidates[0];
}

// used for income/bill type abstraction based on category and isBill/Subscription flags
export function isIncomeFromTxn(
  txn: TransactionProps | null | undefined,
  incomeCOAs: { has: (id: string) => boolean } | null | undefined,
): boolean {
  if (!txn) return false;
  if (txn.isSubscription || txn.isBill) return false;
  if (txn.coa) {
    return incomeCOAs?.has(txn.coa.id) ?? false;
  }
  if (isSplitTxn(txn)) {
    // split
    return Boolean(
      txn.split?.items &&
        txn.split.items.find((item) => Boolean(item.coa && item.coa.id && incomeCOAs?.has(item.coa.id))),
    );
  }
  return false;
}

// Collapses every "no business" spelling ('', '0', 'PERSONAL', null) to 'PERSONAL', matching a USAGE filter's id.
export const usageIdFromBusinessId = (businessId: string | null | undefined): string =>
  accountsUtils.normalizeBusinessIdForUsage(businessId) ?? 'PERSONAL';

// Undefined usageIds means no filter is applied.
export const isBusinessIdInUsages = (businessId: string | null | undefined, usageIds?: ReadonlySet<string>): boolean =>
  !usageIds || usageIds.has(usageIdFromBusinessId(businessId));

// A mixed-usage split matches if any line does; callers totaling amounts must filter lines via isBusinessIdInUsages.
export function isTransactionInUsages(
  txn: TransactionProps | null | undefined,
  usageIds?: ReadonlySet<string>,
): boolean {
  if (!usageIds) return true;
  if (!txn) return false;
  if (isSplitTxn(txn)) {
    return Boolean(txn.split?.items?.find((item) => isBusinessIdInUsages(item?.businessId, usageIds)));
  }
  return isBusinessIdInUsages(txn.businessId, usageIds);
}

export const InvestmentTxnActionsByType = Object.freeze({
  [InvestmentTxnType.BUY]: 'Buy',
  [InvestmentTxnType.SELL]: 'Sell',
  [InvestmentTxnType.STOCK_DIVIDEND]: 'Stock Dividend',
  [InvestmentTxnType.REINVEST_DIVIDEND]: 'Reinvest Dividend',
  [InvestmentTxnType.REINVEST_INTEREST]: 'Reinvest Interest',
  [InvestmentTxnType.REINVEST_CAPITAL_GAIN_LONG]: 'Reinvest Long-term Capital Gain',
  [InvestmentTxnType.REINVEST_CAPITAL_GAIN_SHORT]: 'Reinvest Short-term Capital Gain',
  [InvestmentTxnType.INCOME_DIVIDEND]: 'Income Dividend',
  [InvestmentTxnType.INCOME_INTEREST]: 'Income Interest',
  [InvestmentTxnType.CAPITAL_GAIN_LONG]: 'Income Long-term Capital Gain',
  [InvestmentTxnType.CAPITAL_GAIN_SHORT]: 'Income Short-term Capital Gain',
  [InvestmentTxnType.INCOME_MISCELLANEOUS]: 'Income Miscellaneous',
  [InvestmentTxnType.ADD_SHARES]: 'Add Shares',
  [InvestmentTxnType.REMOVE_SHARES]: 'Remove Shares',
  [InvestmentTxnType.BUY_BONDS]: 'Buy Bonds',
  [InvestmentTxnType.SELL_BONDS]: 'Sell Bonds',
  [InvestmentTxnType.EXPENSE_MARGIN_INTEREST]: 'Margin Interest Expense',
  [InvestmentTxnType.EXPENSE_MISCELLANEOUS]: 'Miscellaneous Expense',
  [InvestmentTxnType.RETURN_OF_CAPITAL]: 'Return of Capital',
  [InvestmentTxnType.STOCK_SPLIT]: 'Stock Split',
  [InvestmentTxnType.SHORT_SELL]: 'Short Sell',
  [InvestmentTxnType.COVER_SHORT_SELL]: 'Buy to Cover',
  [InvestmentTxnType.UNKNOWN]: '',
  VIRTUAL_CASH_FLOW: 'Payment/Deposit',
  VIRTUAL_TRANSFER_SHARES: 'Transfer Shares',
});

// The share description's own price formula. Deliberately not calcPricePerUnit, which
// switches on txn type and would change the rendered string.
const shareDescriptionPricePerUnit = (transaction: TransactionProps) =>
  Math.abs(
    ((transaction.amount || transaction.costBasis || 0) - (transaction.commission || 0)) / (transaction.units || 1),
  );

/**
 * Whether a share description ("23 AAPL shares @ 1.0000") can be built for this txn.
 * False for the cash-like investment types that carry no units (income/expense/dividend
 * postings), where `investmentTransactionDescription` falls back to the payee.
 */
export const hasInvestmentShareDescription = (transaction: TransactionProps, securityName?: string | null) =>
  Boolean(
    isValidInvestmentTxnType(transaction.investmentTxnType) &&
      transaction.units &&
      (transaction.symbol || securityName || transaction.securityName) &&
      typeof shareDescriptionPricePerUnit(transaction) === 'number',
  );

export const investmentTransactionDescription = (transaction: TransactionProps, securityName?: string | null) => {
  let description = '-';
  // NOTE: txns of manual securities do not have the securityName property
  const security = transaction.symbol || securityName || transaction.securityName;
  const pricePerUnit = shareDescriptionPricePerUnit(transaction);
  if (hasInvestmentShareDescription(transaction, securityName)) {
    // units is guaranteed non-zero by hasInvestmentShareDescription
    const units = Math.abs(transaction.units || 0);
    if (pricePerUnit) {
      description = `${units} ${security} shares @ ${pricePerUnit?.toFixed?.(4)}`;
    } else {
      description = `${units} ${security} shares`;
    }
  } else if (transaction.payee) {
    description = transaction.payee;
  }
  return description;
};

export function isBankDownloadedTxn(txn: TransactionProps): boolean {
  return Boolean(txn && txn.cpData);
}

export function isMatched(txn: TransactionProps): boolean {
  return Boolean(txn && txn.matchState && (txn.matchState === 'AUTO_MATCHED' || txn.matchState === 'USER_MATCHED'));
}

export const isAcceptedScheduledTxn = (txn: TransactionProps | TransactionRecord): boolean =>
  txn.source === 'SCHEDULED_TRANSACTION';
export const isUnacceptedScheduledTxn = (
  txn: TransactionProps | TransactionRecord | TransactionItemCombinedProps,
): boolean => txn.source === 'SCHEDULED_TRANSACTION_PENDING';
export const isTxnMatchedOrLinked = (txn: TransactionProps | TransactionRecord | null | undefined): boolean =>
  Boolean(txn?.matchedTxn?.id) || txn?.matchState === 'AUTO_MATCHED' || txn?.matchState === 'USER_MATCHED';

// Is the reminder matched/linked, or marked as accepted
export const isRealizedReminder = (txn: TransactionProps | TransactionRecord | null | undefined): boolean =>
  !txn || !isUnacceptedScheduledTxn(txn) || isTxnMatchedOrLinked(txn);

// A reminder (SCHEDULED_TRANSACTION_PENDING) that has matched a downloaded transaction carries the
// downloaded id in `matchedTxn.id`. The store can retain both records at once (e.g. while the download
// is still bank-side PENDING), so callers use this to avoid double-counting the pair.
export const getMatchedTxnIdsFromReminders = (
  transactions: Iterable<TransactionProps | TransactionRecord>,
): Set<string> => {
  const matchedTxnIds = new Set<string>();
  for (const txn of transactions) {
    const matchedId = txn.matchedTxn?.id;
    if (isUnacceptedScheduledTxn(txn) && matchedId) {
      matchedTxnIds.add(matchedId);
    }
  }
  return matchedTxnIds;
};

export const isTxnMatchedToSalesReceipt = ({
  txnId,
  transactionsById,
}: {
  txnId: string | null | undefined;
  transactionsById: ImmutableMap<string, TransactionRecord> | undefined;
}): boolean =>
  Boolean(
    txnId &&
      transactionsById?.find(
        (txn) =>
          txn?.type === 'INVOICE' && txn?.subtype === TransactionSubType.SalesReceipt && txn?.matchedTxn?.id === txnId,
      ),
  );

export const isA2aTransferTxn = (
  txn: TransactionProps | TransactionRecord | TransactionItemCombinedProps | null | undefined,
): boolean => txn?.subtype === TransactionSubType.A2aTransfer;
export const isCancelledA2aTransfer = (txn: TransactionProps): boolean => txn?.subtype === 'A2A_SHADOW_TXN';
export const isPlannedA2ATransfer = (
  txn: TransactionProps | TransactionRecord | TransactionItemCombinedProps,
): boolean => isA2aTransferTxn(txn) && txn?.matchState === 'NOT_MATCHED';
export const isUnacceptedReminderTxn = (
  txn: TransactionProps | TransactionRecord | TransactionItemCombinedProps,
): boolean => isUnacceptedScheduledTxn(txn) || isPlannedA2ATransfer(txn);

export const isRefundTxn = (txn: TransactionProps | TransactionItemCombinedProps): boolean =>
  txn?.subtype === 'REFUND_SHADOW_TXN' || txn?.subtype === 'REFUND';
/** Expected and overdue refunds are both incomplete, so this never needs today's date. */
export const isRefundIncompleteTxn = (txn: TransactionProps): boolean =>
  txn?.subtype === 'REFUND_SHADOW_TXN' ||
  (txn?.subtype === 'REFUND' && (txn.matchState === 'NOT_MATCHED' || txn.matchState === 'USER_UNMATCHED'));

export const RefundState = Object.freeze({
  EXPECTED: 'EXPECTED',
  OVERDUE: 'OVERDUE',
  COMPLETE: 'COMPLETE',
  SHADOW: 'SHADOW',
  NONREFUND: 'NONREFUND',
});

// All the nested transaction fields can trigger errors when clients are using plain js versions, just using the specific fields this utility actually needs makes it easier
export const getRefundState = (
  transaction: Pick<TransactionProps, 'subtype' | 'matchState' | 'postedOn'> | null,
  // Really this should be required but it would be a messy breaking change to chase down
  todayISO?: string,
) => {
  let refundState;

  switch (transaction?.subtype) {
    case 'REFUND':
      switch (transaction.matchState) {
        case 'NOT_MATCHED':
        case 'USER_UNMATCHED':
          {
            if (transaction.postedOn < (todayISO || DateTime.local().toISODate())) {
              refundState = RefundState.OVERDUE;
            } else {
              refundState = RefundState.EXPECTED;
            }
          }
          break;
        case 'AUTO_MATCHED':
        case 'USER_MATCHED':
        default:
          refundState = RefundState.COMPLETE;
      }
      break;
    case 'REFUND_SHADOW_TXN':
      refundState = RefundState.SHADOW;
      break;
    default:
      refundState = RefundState.NONREFUND;
  }

  return refundState;
};

// -------------------------------------------------------
// isUpcoming
export function isUpcoming(
  txn: TransactionProps | TransactionItemCombinedProps | null | undefined,
  today = DateTime.local().toISODate(),
): boolean {
  return Boolean(
    txn &&
      (txn.source === 'SCHEDULED_TRANSACTION_PENDING' ||
        txn.postedOn > (today ?? '') ||
        ((isRefundTxn(txn) || isPlannedA2ATransfer(txn)) &&
          txn?.matchState !== 'AUTO_MATCHED' &&
          txn?.matchState !== 'USER_MATCHED')),
  );
}

export const isGoalTxnInNonGoalAccount = (
  txn: TransactionProps,
  goalAccountIds: { contains: (id: string) => boolean },
) =>
  txn.coa &&
  txn.coa.type === 'ACCOUNT' &&
  goalAccountIds.contains(txn.coa.id) &&
  !goalAccountIds.contains(txn.accountId || '');

//-----------------------------------------------------------------------
//
// getMatchState
//
// For any transaction will return the following
//
// 'manual' - the transaction has no cpData
// 'matched' - the transaction has cpData and is matchstate is one of the MATCHED states
// 'downloaded' - the transaction has cpData, is not matched, and is not reviewed
//
export function getMatchState(txn: TransactionProps) {
  if (txn.cpData) {
    if (txn.matchState === 'AUTO_MATCHED' || txn.matchState === 'USER_MATCHED') {
      return 'matched';
    }
    return 'downloaded';
  }
  return 'manual';
}

/*
 * getTxnStateInfo
 *
 * This is a mapping from txn SOURCE and STATUS to an object that indicates display information for the transaction
 * object returns contains 'entry', which is either 'bank', 'matched', 'manual', or 'scheduled', and a status which is a combination of
 * transaction state and source, and is one of the following:
 * PENDING
 * CLEARED
 * UPCOMING
 * DUE (Due Today)
 * OVERDUE
 */
export function getTxnStateInfo(txn: TransactionProps) {
  const today = DateTime.local().startOf('day');
  const txnDay = DateTime.fromISO(txn.postedOn).startOf('day');
  const future = txnDay > today;

  const retObj: {
    entry: 'bank' | 'manual' | 'matched' | 'scheduled' | 'unmatched';
    status?: 'PENDING' | 'CLEARED' | 'UPCOMING' | 'DUE' | 'OVERDUE' | 'RECONCILED';
  } = {
    entry: getMatchState(txn) === 'matched' ? 'matched' : 'bank',
    status: txn.state,
  };

  switch (txn.source && txn.source.trim()) {
    case 'QCS_REFRESH':
    case 'QCS_BATCH_REFRESH':
    case 'CLIENT_AGGREGATED':
      break;

    case 'QCS_REFRESH_BANK_PENDING':
    case 'QCS_BATCH_REFRESH_BANK_PENDING':
    case 'CLIENT_AGGREGATED_BANK_PENDING':
    case 'REAL_TIME':
      break;

    case 'USER_ENTERED':
      retObj.entry = getMatchState(txn) === 'matched' ? 'matched' : 'manual';
      if (txn.state === 'CLEARED' || txn.state === 'RECONCILED') {
        retObj.status = txn.state;
      } else {
        retObj.status = future ? 'UPCOMING' : 'PENDING';
      }
      break;

    case 'USER_OWNED_BANK_PENDING':
    case 'USER_OWNED_REAL_TIME_BANK_PENDING':
      break;

    case 'SCHEDULED_TRANSACTION_PENDING':
      retObj.status = future ? 'UPCOMING' : 'OVERDUE';
      if (txnDay.hasSame(today, 'day')) {
        retObj.status = 'DUE';
      }
      retObj.entry = retObj.entry === 'matched' ? 'matched' : 'scheduled';
      break;

    case 'SCHEDULED_TRANSACTION_UNKNOWN':
    case 'SCHEDULED_TRANSACTION': {
      if (txn.state === 'PENDING') {
        retObj.status = future ? 'UPCOMING' : 'PENDING';
      } else {
        retObj.status = 'CLEARED';
      }
      retObj.entry = retObj.entry === 'matched' ? 'matched' : 'scheduled';
      break;
    }

    case 'SCHEDULED_TRANSACTION_SKIPPED':
      // not applicable, this transaction should not be shown (soft deleted)
      break;

    case 'UNKNOWN':
      // should never happen - throw an event?
      retObj.entry = 'manual';
      retObj.status = future ? 'UPCOMING' : 'CLEARED';
      break;

    default:
      // this should REALLY never happen
      break;
  }

  return retObj;
}

export const isManualTransaction = (transaction: TransactionProps) =>
  transaction.source === 'USER_ENTERED' && transaction.matchState === 'NOT_MATCHED';

export const normalizeInvestmentTransaction = (investmentTransaction: TransactionProps) => {
  const investmentTransactionNormalized = { ...investmentTransaction };

  investmentTransactionNormalized.type = 'INVESTMENT';

  // units
  switch (investmentTransaction.investmentTxnType) {
    case InvestmentTxnType.BUY:
    case InvestmentTxnType.REINVEST_DIVIDEND:
    case InvestmentTxnType.REINVEST_INTEREST:
    case InvestmentTxnType.REINVEST_CAPITAL_GAIN_LONG:
    case InvestmentTxnType.REINVEST_CAPITAL_GAIN_SHORT:
    case InvestmentTxnType.ADD_SHARES:
    case InvestmentTxnType.BUY_BONDS:
    case InvestmentTxnType.COVER_SHORT_SELL:
    case InvestmentTxnType.STOCK_DIVIDEND:
      investmentTransactionNormalized.units = Math.abs(investmentTransaction.units ?? 0);
      break;
    case InvestmentTxnType.SELL:
    case InvestmentTxnType.REMOVE_SHARES:
    case InvestmentTxnType.SELL_BONDS:
    case InvestmentTxnType.SHORT_SELL:
      investmentTransactionNormalized.units = -Math.abs(investmentTransaction.units ?? 0);
      break;
    case InvestmentTxnType.INCOME_DIVIDEND:
    case InvestmentTxnType.INCOME_INTEREST:
    case InvestmentTxnType.CAPITAL_GAIN_LONG:
    case InvestmentTxnType.CAPITAL_GAIN_SHORT:
    case InvestmentTxnType.INCOME_MISCELLANEOUS:
    case InvestmentTxnType.EXPENSE_MARGIN_INTEREST:
    case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
    case InvestmentTxnType.RETURN_OF_CAPITAL:
    case InvestmentTxnType.UNKNOWN:
      investmentTransactionNormalized.units = 0;
      break;
    case InvestmentTxnType.STOCK_SPLIT:
      investmentTransactionNormalized.units =
        Math.abs(investmentTransaction.units ?? 0) *
        Math.sign((investmentTransaction.splitNumerator ?? 0) - (investmentTransaction.splitDenominator ?? 0));
      break;
    default:
  }

  // amount
  switch (investmentTransaction.investmentTxnType) {
    case InvestmentTxnType.BUY:
    case InvestmentTxnType.BUY_BONDS:
    case InvestmentTxnType.COVER_SHORT_SELL:
    case InvestmentTxnType.EXPENSE_MARGIN_INTEREST:
    case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
      investmentTransactionNormalized.amount = -Math.abs(investmentTransaction.amount ?? 0);
      break;
    case InvestmentTxnType.SELL:
    case InvestmentTxnType.INCOME_DIVIDEND:
    case InvestmentTxnType.INCOME_INTEREST:
    case InvestmentTxnType.CAPITAL_GAIN_LONG:
    case InvestmentTxnType.CAPITAL_GAIN_SHORT:
    case InvestmentTxnType.INCOME_MISCELLANEOUS:
    case InvestmentTxnType.RETURN_OF_CAPITAL:
    case InvestmentTxnType.SELL_BONDS:
    case InvestmentTxnType.SHORT_SELL:
      investmentTransactionNormalized.amount = +Math.abs(investmentTransaction.amount ?? 0);
      break;
    case InvestmentTxnType.STOCK_DIVIDEND:
    case InvestmentTxnType.ADD_SHARES:
    case InvestmentTxnType.REMOVE_SHARES:
    case InvestmentTxnType.REINVEST_DIVIDEND:
    case InvestmentTxnType.REINVEST_INTEREST:
    case InvestmentTxnType.REINVEST_CAPITAL_GAIN_LONG:
    case InvestmentTxnType.REINVEST_CAPITAL_GAIN_SHORT:
    case InvestmentTxnType.STOCK_SPLIT:
    case InvestmentTxnType.UNKNOWN:
      investmentTransactionNormalized.amount = 0;
      break;
    default:
  }

  // costBasis
  switch (investmentTransaction.investmentTxnType) {
    case InvestmentTxnType.BUY:
    case InvestmentTxnType.SHORT_SELL:
    case InvestmentTxnType.ADD_SHARES:
    case InvestmentTxnType.REINVEST_DIVIDEND:
    case InvestmentTxnType.REINVEST_INTEREST:
    case InvestmentTxnType.REINVEST_CAPITAL_GAIN_LONG:
    case InvestmentTxnType.REINVEST_CAPITAL_GAIN_SHORT:
      if (investmentTransactionNormalized.amount) {
        investmentTransactionNormalized.costBasis = investmentTransactionNormalized.amount;
      } else {
        investmentTransactionNormalized.costBasis = -Math.abs(investmentTransactionNormalized.costBasis ?? 0);
      }
      break;
    case InvestmentTxnType.COVER_SHORT_SELL:
    case InvestmentTxnType.EXPENSE_MARGIN_INTEREST:
    case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
    case InvestmentTxnType.SELL:
    case InvestmentTxnType.INCOME_DIVIDEND:
    case InvestmentTxnType.INCOME_INTEREST:
    case InvestmentTxnType.CAPITAL_GAIN_LONG:
    case InvestmentTxnType.CAPITAL_GAIN_SHORT:
    case InvestmentTxnType.INCOME_MISCELLANEOUS:
    case InvestmentTxnType.RETURN_OF_CAPITAL:
    case InvestmentTxnType.SELL_BONDS:
    case InvestmentTxnType.STOCK_DIVIDEND:
    case InvestmentTxnType.REMOVE_SHARES:
    case InvestmentTxnType.STOCK_SPLIT:
    case InvestmentTxnType.UNKNOWN:
      investmentTransactionNormalized.costBasis = 0;
      break;
    case InvestmentTxnType.BUY_BONDS: // cost basis different from amount caused by accrued interest
      break;
    default:
  }

  // commission
  switch (investmentTransaction.investmentTxnType) {
    case InvestmentTxnType.ADD_SHARES:
    case InvestmentTxnType.EXPENSE_MARGIN_INTEREST:
    case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
    case InvestmentTxnType.CAPITAL_GAIN_LONG:
    case InvestmentTxnType.CAPITAL_GAIN_SHORT:
    case InvestmentTxnType.INCOME_DIVIDEND:
    case InvestmentTxnType.INCOME_INTEREST:
    case InvestmentTxnType.INCOME_MISCELLANEOUS:
    case InvestmentTxnType.REMOVE_SHARES:
    case InvestmentTxnType.RETURN_OF_CAPITAL:
    case InvestmentTxnType.STOCK_DIVIDEND:
    case InvestmentTxnType.STOCK_SPLIT:
    case InvestmentTxnType.UNKNOWN:
      if (investmentTransactionNormalized.commission) {
        investmentTransactionNormalized.commission = 0;
      }
      break;
    default:
  }

  // splitNumerator + splitDenominator
  switch (investmentTransaction.investmentTxnType) {
    case InvestmentTxnType.STOCK_SPLIT:
      investmentTransactionNormalized.splitNumerator = Math.abs(investmentTransaction.splitNumerator ?? 0);
      investmentTransactionNormalized.splitDenominator = Math.abs(investmentTransaction.splitDenominator ?? 0);
      break;
    default:
      investmentTransactionNormalized.splitNumerator = 0;
      investmentTransactionNormalized.splitDenominator = 0;
  }

  // coa
  switch (investmentTransaction.investmentTxnType) {
    case InvestmentTxnType.UNKNOWN:
    case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
    case InvestmentTxnType.INCOME_MISCELLANEOUS:
      // keep coa
      break;
    default:
      investmentTransactionNormalized.coa = { type: 'UNCATEGORIZED', id: '0' }; // erase
  }

  return investmentTransactionNormalized;
};

export const calcPricePerUnit = (transaction?: TransactionProps | null) => {
  let pricePerUnit: number | undefined;
  if (transaction) {
    const costBasis = transaction?.costBasis || transaction?.amount;
    if (transaction?.units && costBasis) {
      switch (transaction.investmentTxnType) {
        case InvestmentTxnType.BUY:
        case InvestmentTxnType.BUY_BONDS:
        case InvestmentTxnType.COVER_SHORT_SELL:
        case InvestmentTxnType.EXPENSE_MARGIN_INTEREST:
        case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
        case InvestmentTxnType.SELL:
        case InvestmentTxnType.INCOME_DIVIDEND:
        case InvestmentTxnType.INCOME_INTEREST:
        case InvestmentTxnType.CAPITAL_GAIN_LONG:
        case InvestmentTxnType.CAPITAL_GAIN_SHORT:
        case InvestmentTxnType.INCOME_MISCELLANEOUS:
        case InvestmentTxnType.RETURN_OF_CAPITAL:
        case InvestmentTxnType.SELL_BONDS:
        case InvestmentTxnType.SHORT_SELL:
        case InvestmentTxnType.ADD_SHARES:
        case InvestmentTxnType.REINVEST_DIVIDEND:
        case InvestmentTxnType.REINVEST_INTEREST:
        case InvestmentTxnType.REINVEST_CAPITAL_GAIN_LONG:
        case InvestmentTxnType.REINVEST_CAPITAL_GAIN_SHORT:
          pricePerUnit = Math.abs((costBasis - (transaction.commission || 0)) / transaction.units);
          break;
        default:
      }
    }
  }
  return pricePerUnit;
};

export const calcUnits = (transaction: TransactionProps, pricePerUnit: number | string) => {
  let units = 0;
  const pricePerUnitNumber = Number(pricePerUnit);
  const costBasis = Number(transaction?.costBasis || transaction?.amount);
  if (pricePerUnitNumber && !Number.isNaN(pricePerUnitNumber && !Number.isNaN(costBasis)) && costBasis) {
    units = (costBasis - (transaction.commission || 0)) / pricePerUnitNumber;
  }
  return units;
};

export const security = (
  transaction: TransactionProps | null | undefined,
  security: SecurityProps | null | undefined,
) => {
  let { symbol, securityName } = transaction || {};
  if (transaction?.localSecurityId) {
    const securityFound =
      security || (store ? securitiesSelectors.getResourceById(store.getState(), transaction.localSecurityId) : false);
    if (securityFound) {
      symbol = securityFound.symbol;
      securityName = securityFound.name;
    }
  }
  return symbol && securityName ? `${symbol} - ${securityName}` : symbol || securityName || undefined;
};

const invalidInvestmentTxnTypes = [InvestmentTxnType.UNKNOWN, undefined, null, '', 'VIRTUAL_CASH_FLOW'] as const;
export const isValidInvestmentTxnType = (investmentTxnType: any) =>
  !invalidInvestmentTxnTypes.includes(investmentTxnType);

export const isInvestmentUnitsEditable = (investmentTxnType: string | null | undefined) => {
  switch (investmentTxnType) {
    case InvestmentTxnType.EXPENSE_MARGIN_INTEREST:
    case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
    case InvestmentTxnType.CAPITAL_GAIN_LONG:
    case InvestmentTxnType.CAPITAL_GAIN_SHORT:
    case InvestmentTxnType.INCOME_DIVIDEND:
    case InvestmentTxnType.INCOME_INTEREST:
    case InvestmentTxnType.INCOME_MISCELLANEOUS:
    case InvestmentTxnType.RETURN_OF_CAPITAL:
    case InvestmentTxnType.STOCK_SPLIT: // TODO: figure out if we want to use units for adjustment
    case InvestmentTxnType.UNKNOWN:
    case undefined:
    case null:
    case 'VIRTUAL_CASH_FLOW':
      return false;
    default:
      return true;
  }
};

export const isInvestmentPricePerUnitEditable = (investmentTxnType: string | null | undefined) => {
  switch (investmentTxnType) {
    case InvestmentTxnType.EXPENSE_MARGIN_INTEREST:
    case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
    case InvestmentTxnType.CAPITAL_GAIN_LONG:
    case InvestmentTxnType.CAPITAL_GAIN_SHORT:
    case InvestmentTxnType.INCOME_DIVIDEND:
    case InvestmentTxnType.INCOME_INTEREST:
    case InvestmentTxnType.INCOME_MISCELLANEOUS:
    case InvestmentTxnType.REMOVE_SHARES:
    case InvestmentTxnType.RETURN_OF_CAPITAL:
    case InvestmentTxnType.STOCK_DIVIDEND:
    case InvestmentTxnType.STOCK_SPLIT:
    case InvestmentTxnType.UNKNOWN:
    case undefined:
    case null:
    case 'VIRTUAL_CASH_FLOW':
      return false;
    default:
      return true;
  }
};

export const isInvestmentCommissionEditable = (investmentTxnType: string | null | undefined) => {
  switch (investmentTxnType) {
    case InvestmentTxnType.ADD_SHARES:
    case InvestmentTxnType.EXPENSE_MARGIN_INTEREST:
    case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
    case InvestmentTxnType.CAPITAL_GAIN_LONG:
    case InvestmentTxnType.CAPITAL_GAIN_SHORT:
    case InvestmentTxnType.INCOME_DIVIDEND:
    case InvestmentTxnType.INCOME_INTEREST:
    case InvestmentTxnType.INCOME_MISCELLANEOUS:
    case InvestmentTxnType.REMOVE_SHARES:
    case InvestmentTxnType.RETURN_OF_CAPITAL:
    case InvestmentTxnType.STOCK_DIVIDEND:
    case InvestmentTxnType.STOCK_SPLIT:
    case InvestmentTxnType.UNKNOWN:
    case undefined:
    case null:
    case 'VIRTUAL_CASH_FLOW':
      return false;
    default:
      return true;
  }
};

export const isInvestmentAmountRequired = (investmentTxnType: string | null | undefined) => {
  switch (investmentTxnType) {
    case InvestmentTxnType.INCOME_DIVIDEND:
    case InvestmentTxnType.INCOME_INTEREST:
    case InvestmentTxnType.CAPITAL_GAIN_LONG:
    case InvestmentTxnType.CAPITAL_GAIN_SHORT:
    case InvestmentTxnType.INCOME_MISCELLANEOUS:
    case InvestmentTxnType.EXPENSE_MARGIN_INTEREST:
    case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
    case InvestmentTxnType.RETURN_OF_CAPITAL:
    case InvestmentTxnType.UNKNOWN:
      return true;
    default:
      return false;
  }
};

const searchDateFormats = ['M/d/yyyy', 'MMMd,yyyy', 'MMMMd,yyyy', 'M/yyyy', 'MMM,yyyy', 'MMMMyyyy', 'MMMyyyy'] as const;

const cleanString = (str: string) => str.split(' ').join('');

const matchesNoProjectSearch = (txn: TransactionProps): boolean =>
  !txn.projectId && !(txn.split?.items?.some((item) => Boolean(item.projectId)) ?? false);

function isValidDate(dateString: string) {
  // Every search date format has a numeric year.
  if (!/\d/.test(dateString)) return false;
  return searchDateFormats.some((fmt) => DateTime.fromFormat(cleanString(dateString), fmt).isValid);
}

// Luxon and numeral are slow per row on Hermes, and postedOn and amount values repeat across rows.
const getSearchDateText = weakMapMemoize((postedOn: string) => DateTime.fromISO(postedOn).toFormat('MMM dd,yyyy'));
const getSearchAmountText = weakMapMemoize((amount: number | null | undefined) =>
  String(numeral(amount).format('$0,00.00')),
);
const getPostedOnDay = weakMapMemoize((postedOn: string) => {
  const date = DateTime.fromISO(postedOn);
  // Only a plain date that parses back to itself (not a timestamp or a skipped local day) is a calendar day.
  return { year: date.year, isDateOnly: date.toISODate() === postedOn };
});
[getSearchDateText, getSearchAmountText, getPostedOnDay].forEach(registerMemoizedCache);

function convertFilterToDateRange(dateString: string) {
  let val1 = DateTime.local();
  let val2 = val1;

  searchDateFormats.some((fmt) => {
    const dt = DateTime.fromFormat(cleanString(dateString), fmt);
    if (dt.isValid) {
      val1 = dt;
      if (fmt.indexOf('d') !== -1) {
        val2 = dt;
      } else {
        val2 = dt.plus({ months: 1 });
      }
    }
    return false;
  });
  return { val1, val2 };
}

export function makeTagsString(
  tags?: ImmutableList<tagsTypes.TagProps> | Array<tagsTypes.TagProps> | null,
  useHashes?: boolean,
) {
  let str: string | null = null;
  if (tags) {
    tags.forEach((x) => {
      const tag = (store ? tagsSelectors.getTagById(store.getState(), getId(x)) : null) || x;
      if (tag && tag.name) {
        str = str ? `${str}${useHashes ? ' ' : '/'}` : '';
        str = `${str}${useHashes ? `#${tag.name}` : tag.name}`;
      }
    });
  }
  return str;
}

export function makeProjectString(projectId?: string | null) {
  return projectId && store ? getProjectNameById(store?.getState(), projectId) : null;
}

export function makeCustomerString(customerId?: string | null) {
  if (!customerId) {
    return null;
  }
  const customer = customerId && store ? customersSelectors.getCustomerById(store?.getState(), customerId) : null;
  const address = customer?.address
    ? `${customer.address.city ?? ''}
    ${customer.address.state ?? ''} ${customer.address.zipCode ?? ''} ${customer.address.line1 ?? ''}
    ${customer.address.line2 ?? ''} ${customer.address.country ?? ''}`
    : null;

  return `${customer?.businessName ?? ''} ${customer?.firstName ?? ''} ${customer?.lastName ?? ''} ${address}`?.trim();
}

/**
 * SMART SEARCH tricks
 *
 *
 * If the string typed in evaluates to a number (Not NaN), then we treat it as a number.
 *
 * If the user starts expression with a '-' or a '+' it restricts the search to positive or negative values only
 * If the user types an & sign before the number, we look for an amount in the range of the 2 following comma separated values(Not comma separated anymore, now separated by ".to.")
 * If the user types a >, we look for all income and expenses whose absolute value is greater than the amount given
 * If the user types a <, we look for all income and expenses whose absolute value is less than the amount given
 * If the user types a ~ sign before the expression, or adds 'ish' to the end of it, we look for values relatively close to the value
 *
 * If the filter typed in is a valid DATE, then DATE SEARCH is used
 * >date is all dates after this date
 * <date is all dates before this date
 * ~ at the beginning, or 'ish' added to the end will search 15 days on either side of the date
 * &date,date will search for dates between the 2 given dates
 *
 * For TEXT searches, we remove all punctuation characters (from both the search string, and the string bases search targets
 * UNLESS the user types a '=' as the first character, then an exact string match is considered, including case
 * ALL text searching is case insensitive
 *
 * You may combine expressions using the boolean 'AND' operator expressed as .and.
 *   "+>02/23/2020 .and. Amazon"
 *
 * If the filter expression (can be between .AND. and .OR.) is 'reviewed' or 'notreviewed' then it will filter based on reviewed status
 *
 */
type ParsedExpression = {
  fuzzy: boolean;
  numberSearch: boolean;
  dateSearch: boolean;
  negOnly: boolean;
  posOnly: boolean;
  prefix: string;
  val1: DateTime | number | null;
  val2: DateTime | number | null;
  baseValue: number;
  exactDecimals: boolean;
  filterClean: string;
  // Per-query values the row matcher would otherwise recompute for every transaction.
  numericText: string;
  val1Day: string | null;
  val2Day: string | null;
  needle: string;
  isNoProjectSearch: boolean;
};
const minYear = 1990;
const maxYear = 2035;
function isValidSearchYear(value: number): boolean {
  if (!Number.isInteger(value)) {
    return false;
  }
  return value >= minYear && value <= maxYear;
}
function sanitizeForSearch(input: string): string {
  return (
    input
      // Remove apostrophes and backticks
      .replace(/['`]/g, '')
      // Replace special characters with spaces
      .replace(/([!"#%&*()+,\-./:;<=>?\][@\\^_])/g, ' ')
  );
}

// -------------------------------------
// HELPER: Parse filter expression
// -------------------------------------
function parseFilterExpression(_filter: string): ParsedExpression {
  let filter = _filter;
  let fuzzy = false;

  // Is this a fuzzy search?
  if (filter.slice(-3) === 'ish') {
    filter = filter.slice(0, -3);
    fuzzy = true;
  }
  // Does user want a fuzzy search
  if (filter[0] === '~') {
    fuzzy = true;
    filter = filter.slice(1);
  }

  let numberSearch = false;
  let dateSearch = false;
  let val1: DateTime | number | null = null;
  let val2: DateTime | number | null = null;

  // Does user want to restrict values to only income or expense?
  const negOnly = filter[0] === '-';
  const posOnly = filter[0] === '+';
  const startChar = negOnly || posOnly ? 1 : 0;
  let prefix = filter[startChar];

  // Range or comparison search
  if (prefix === '>' || prefix === '<' || prefix === '&') {
    const values = filter.slice(startChar + 1).split('.to.');
    if (!Number.isNaN(Number(values[0]))) {
      val1 = Number(values[0]);
      val2 = values.length > 1 ? Number(values[1]) : 0;
      numberSearch = !Number.isNaN(val1) && !Number.isNaN(val2);
    } else if (isValidDate(values[0])) {
      // date?
      val1 = convertFilterToDateRange(values[0]).val1;
      val2 = values.length > 1 ? convertFilterToDateRange(values[1]).val2 : DateTime.local();
      dateSearch = val1.isValid && val2.isValid;
    }
    // No PREFIX, so if text is a date or a number, apply a special search to them
  } else if (!Number.isNaN(Number(filter))) {
    numberSearch = true;
    if (fuzzy) {
      const filterAsAmount = Number(filter);
      const variancePct = 0.02;
      // construct a range search
      val2 = Number((filterAsAmount * (1 + variancePct)).toFixed(2));
      val1 = Number((filterAsAmount * (1 - variancePct)).toFixed(2));
      prefix = '&';
    }
  } else if (isValidDate(filter)) {
    dateSearch = true;
    const dateSpread = fuzzy ? 5 : 0;
    const dateRange = convertFilterToDateRange(filter);
    val1 = dateRange.val1.minus({ days: dateSpread });
    val2 = dateRange.val2.plus({ days: dateSpread });
    prefix = '&';
  }

  // First, remove apostophes from the search then Convert special characters to spaces for the search
  const filterClean = sanitizeForSearch(filter);

  const baseValue = Number(filter.slice(negOnly || posOnly ? 1 : 0)); // with math operand removed
  const exactDecimals = filter.includes('.');

  return {
    fuzzy,
    numberSearch,
    dateSearch,
    negOnly,
    posOnly,
    prefix,
    val1,
    val2,
    baseValue,
    exactDecimals,
    filterClean,
    numericText: String(baseValue),
    ...(dateSearch ? toDayBounds(val1, val2) : { val1Day: null, val2Day: null }),
    needle: filterClean.toLowerCase(),
    isNoProjectSearch: cleanString(filterClean).toLowerCase() === 'noproject',
  };
}

// Calendar days compare like DateTimes only when a row posted on the bound day parses to exactly the lower
// bound and the upper bound falls within its own day; skipped or repeated midnights keep the DateTime comparison.
function toDayBounds(
  val1: DateTime | number | null,
  val2: DateTime | number | null,
): { val1Day: string | null; val2Day: string | null } {
  const val1Day = DateTime.isDateTime(val1) ? val1.toISODate() : null;
  const val2Day = DateTime.isDateTime(val2) ? val2.toISODate() : null;
  if (
    !val1Day ||
    !val2Day ||
    DateTime.fromISO(val1Day).toMillis() !== (val1 as DateTime).toMillis() ||
    DateTime.fromISO(val2Day).toMillis() > (val2 as DateTime).toMillis() ||
    DateTime.fromISO(val2Day).plus({ days: 1 }).toMillis() <= (val2 as DateTime).toMillis()
  ) {
    return { val1Day: null, val2Day: null };
  }
  return { val1Day, val2Day };
}

function isInSearchRange<T extends string | DateTime>(value: T, prefix: string, val1: T, val2: T): boolean {
  switch (prefix) {
    case '>':
      return value >= val1;
    case '<':
      return value <= val1;
    case '&':
      return value >= val1 && value <= val2;
    default:
      return false;
  }
}

// The text depends only on the transaction and the names it reads through the store, so it is cached per
// record, and a change to any of those lookups starts a new cache. The inputs are wrapped because of
// import cycles: the selectors aren't defined yet when this module loads.
const selectSearchTextBuilder = createSelector(
  [
    (state: RootState) => categoriesSelectors.getCategoriesById(state),
    (state: RootState) => accountsSelectors.getAccountNamesById(state),
    (state: RootState) => tagsSelectors.getTags(state),
    (state: RootState) => getProjects(state),
    (state: RootState) => customersSelectors.getCustomersWithArchived(state),
  ],
  () => weakMapMemoize(buildTransactionSearchText),
);
[selectSearchTextBuilder, selectSearchTextBuilder.memoizedResultFunc].forEach(registerMemoizedCache);

const getTransactionSearchText = (txn: TransactionProps): string =>
  store ? selectSearchTextBuilder(store.getState())(txn) : buildTransactionSearchText(txn);

// -------------------------------------
// HELPER: Check txn against parsed filter
// -------------------------------------
function matchesParsedExpression(txn: TransactionProps, parsed: ParsedExpression): boolean {
  const {
    numberSearch,
    dateSearch,
    negOnly,
    posOnly,
    prefix,
    val1,
    val2,
    baseValue,
    exactDecimals,
    filterClean,
    numericText,
    val1Day,
    val2Day,
    needle,
    isNoProjectSearch,
  } = parsed;

  /*
   * AMOUNT SEARCH
   */
  if (numberSearch) {
    if (
      txn.type === 'INVESTMENT' &&
      (baseValue === (exactDecimals ? Math.abs(txn.units ?? 0) : Math.floor(Math.abs(txn.units ?? 0))) ||
        baseValue === (exactDecimals ? Math.abs(txn.costBasis ?? 0) : Math.floor(Math.abs(txn.costBasis ?? 0))) ||
        baseValue === (exactDecimals ? Math.abs(txn.commission ?? 0) : Math.floor(Math.abs(txn.commission ?? 0))))
    ) {
      return true;
    }

    if ((negOnly && (txn.amount ?? 0) >= 0) || (posOnly && (txn.amount ?? 0) <= 0)) {
      return false;
    }
    if (isValidSearchYear(baseValue)) {
      if (getPostedOnDay(txn.postedOn ?? '').year === baseValue) {
        return true;
      }
    }
    if (prefix !== '>' && prefix !== '<' && prefix !== '&') {
      if (txn.invoiceNo && txn.invoiceNo.includes(numericText)) {
        return true;
      }
      if (txn.payee && txn.payee.includes(numericText)) {
        return true;
      }
      if (txn.memo && txn.memo.includes(numericText)) {
        return true;
      }
      if (txn.referenceNo && txn.referenceNo.includes(numericText)) {
        return true;
      }
      if (txn.split?.items) {
        for (const item of txn.split.items) {
          if (item.memo && item.memo.includes(numericText)) {
            return true;
          }
        }
      }
    }
    const txnAmt = Math.abs(Number(txn.amount));
    switch (prefix) {
      case '>':
        return txnAmt >= (val1 as number);
      case '<':
        return txnAmt <= (val1 as number);
      case '&':
        return txnAmt >= Math.min(val1 as number, val2 as number) && txnAmt <= Math.max(val1 as number, val2 as number);
      default:
        return String(exactDecimals ? txnAmt : Math.floor(txnAmt)).includes(String(baseValue));
    }
  }

  /*
   * DATE SEARCH
   */
  if (dateSearch) {
    const postedOn = txn.postedOn ?? '';
    if (getPostedOnDay(postedOn).isDateOnly && val1Day && val2Day) {
      // See toDayBounds.
      return isInSearchRange(postedOn, prefix, val1Day, val2Day);
    }
    return isInSearchRange(DateTime.fromISO(postedOn), prefix, val1 as DateTime, val2 as DateTime);
  }

  /*
   * NON-AMOUNT SEARCH
   */
  if (filterClean === 'reviewed' || filterClean === 'notreviewed') {
    return txn.isReviewed ? filterClean === 'reviewed' : filterClean !== 'reviewed';
  }

  if (isNoProjectSearch) {
    return matchesNoProjectSearch(txn);
  }

  return getTransactionSearchText(txn).includes(needle);
}

function buildTransactionSearchText(txn: TransactionProps): string {
  let descString = `${txn.payee}~${getSearchDateText(txn.postedOn ?? '')}~`;
  if (txn.coa) {
    // TODO leverage longCats txlist preference to match search to display of category
    descString += `${chartOfAccountsSelectors.getCoaStringSelector(undefined, txn.coa)}~`;
  }
  descString += `${txn.state}~`;
  if (txn.check?.number) {
    descString += `${txn.check.number}~`;
  }
  // investments meta data
  if (txn.type === 'INVESTMENT') {
    const humanReadableType = txn.investmentTxnType ? InvestmentTxnActionsByType[txn.investmentTxnType] : '';
    descString += `${humanReadableType}~${txn.symbol}~${txn.securityName}~`;
  }
  descString += `${getSearchAmountText(txn.amount)}~`;

  if (txn.split?.items) {
    // TODO leverage longCats txlist preference to match search to display of category
    txn.split.items.forEach((item) => {
      descString += `${item.memo}~${String(item.amount)}~${makeTagsString(item.tags)}~${chartOfAccountsSelectors.getCoaStringSelector(undefined, item.coa)}~${makeProjectString(item.projectId)}~${makeCustomerString(item.customerId)}~`;
    });
  }
  if (txn.projectId) descString += `${makeProjectString(txn.projectId)}~`;
  else descString += `${'no project'}~`;
  if (txn.customerId) descString += `${makeCustomerString(txn.customerId)}~`;
  if (txn.tags) descString += `${makeTagsString(txn.tags)}~`;
  if (txn.memo) descString += `${txn.memo}~`;
  if (txn.invoiceNo) descString += `${txn.invoiceNo}~`;
  if (txn.referenceNo) descString += `${txn.referenceNo}~`;
  if (txn.paymentMethod) descString += `${txn.paymentMethod}~`;

  // search by id for internal purpose
  descString += `${txn.id}~`;

  // First, remove apostophes from the search then Convert special characters to spaces for the search
  return sanitizeForSearch(descString).toLowerCase();
}

// -------------------------------------
// FILTER FUNCTIONS
// -------------------------------------
function splitExpressions(_filter: string): string[] {
  // if multiple expressions, successively filter them
  return _filter
    .split('.AND.')
    .map((f) => f.trim())
    .filter(Boolean);
}
export function getParsedExpressions(filter: string): ParsedExpression[] | null {
  if (!filter || filter.length === 0) return null;
  return splitExpressions(filter).map(parseFilterExpression);
}

function matchesFilter(txn: TransactionProps, parsedExpressions: ParsedExpression[] | null): boolean {
  if (!parsedExpressions || !parsedExpressions.length) return true;
  return parsedExpressions.every((parsed) => matchesParsedExpression(txn, parsed));
}

export function transactionMatchesExpression(
  txn: TransactionProps,
  parsedExpressions: ParsedExpression[] | null,
): boolean {
  if (!parsedExpressions || !parsedExpressions.length) return true;
  return matchesFilter(txn, parsedExpressions);
}

export function filterTransactionsByExpression(
  txns: Array<TransactionProps> | ImmutableList<TransactionProps>,
  _filter: string,
) {
  if (!_filter || _filter.length === 0) return txns;
  const parsedExpressions = getParsedExpressions(_filter);
  // The type signatures of array.filter and list.filter are technically not compatible,
  // but we're only using the first arg of the callback so it doesn't make a difference.
  // this is pointless check is to appease TS.
  return 'size' in txns
    ? txns.filter((txn) => matchesFilter(txn, parsedExpressions))
    : txns.filter((txn) => matchesFilter(txn, parsedExpressions));
}

export function txnItemRepresentsSplitItem(txn: TransactionProps | TransactionItem): boolean {
  return 'item' in txn && Boolean(txn.splitItem);
}

export function txnHasCoa(
  txn: TransactionProps | TransactionItemCombinedProps,
  coaId: string,
  representsSplitItem = false,
) {
  // representsSplitItem=true means this txn IS the split line itself (item.coa is already the split's coa) — check directly.
  // Otherwise (parent-mode or raw TransactionProps) iterate split.items for split transactions.
  if (!representsSplitItem && isSplitTxn(txn)) {
    return txn.split?.items?.find(
      (item) => item.coa?.id === coaId || (item.coa?.type === 'UNCATEGORIZED' && coaId === 'UNCATEGORIZED'),
    );
  }
  return (
    txn.coa?.id === coaId ||
    (txn.coa?.type === 'UNCATEGORIZED' && coaId === 'UNCATEGORIZED') ||
    (txn.coa?.type === 'BALANCE_ADJUSTMENT' && coaId === `BALANCE_ADJUSTMENT_${txn.coa?.id}`)
  );
}

export function txnHasTag(
  txn: TransactionProps | TransactionItemCombinedProps,
  tagId: string,
  representsSplitItem = false,
) {
  if (!representsSplitItem && isSplitTxn(txn)) {
    return txn.split?.items?.find((item) => Boolean(item.tags?.some((x) => x.id === tagId)));
  }
  return txn.tags?.some((x) => x.id === tagId);
}

/*
 Filter Transactions By Filter Object

 Note, the first parameter is an immutable list of transactions

 Filter Object at this time is a dot addressable object (JS object or Immutable Record) with the following optional keys
 coas,                    List of coa objects that include type and id
 tagsIds,                 List of tag ID's that should be included
 payeeNames,                  list of payee names to be included
 flags,                   list of qcs colors or NONE,
 accounts,                list of accountIds to be included (if not specified, it is all accounts)
 businesses,              list of business Ids to be included
 isBillOrSubscription,    if set (not undefined or null), it will filter by whether txns are bills or not
 isExcludedFromReports,    if set (not undefined or null), it will filter by whether txns have their ignored flag set
 dateStart                iso date string or luxon date
 dateEnd                  iso date string or luxon date
 */

type FilterableTransaction = TransactionProps | TransactionItem;

/** Prepares the filter once so each transaction check reads plain values. */
export const createTransactionsFilterByObjFn = (
  filterObject: filtersTypes.FilterObject | null | undefined,
): ((txn: FilterableTransaction) => boolean) => {
  if (!filterObject) return () => true;

  // A failing clause returns `rejected`; the client/project split branch returns its own result.
  const rejected = Boolean(filterObject.filterOut);
  const accepted = !rejected;
  const { advanced } = filterObject;
  const isBillOrSubscriptionFilter = advanced?.isBillOrSubscription;
  const isExcludedFromReportsFilter = advanced?.isExcludedFromReports;
  const isReviewedFilter = advanced?.isReviewed;
  const isExcludedFromF2SFilter = advanced?.isExcludedFromF2S;
  const dateStartISO =
    typeof filterObject.dateStart === 'string' ? filterObject.dateStart : filterObject.dateStart?.toISODate();
  const dateEndISO =
    typeof filterObject.dateEnd === 'string' ? filterObject.dateEnd : filterObject.dateEnd?.toISODate();
  const accountIds = filterObject.accounts?.size ? filterObject.accounts : null;
  const payeeNames = filterObject.payeeNames?.size
    ? new Set(filterObject.payeeNames.map((payee) => payee.toLowerCase()))
    : null;
  const businessIds = filterObject.businesses?.size ? filterObject.businesses : null;
  const wantsPersonal = Boolean(businessIds?.has('PERSONAL') || businessIds?.has('0'));
  const flagValues = filterObject.flags?.size ? filterObject.flags : null;
  const wantsNoFlag = Boolean(flagValues?.has('NONE'));
  // Ids are matched through Array.find, so a falsy id can never match.
  const categoryIds = filterObject.categories?.size ? new Set(filterObject.categories.filter(Boolean)) : null;
  const tagIds = filterObject.tags?.size ? new Set(filterObject.tags.filter(Boolean)) : null;
  const hasCustomersAndProjects = Boolean(filterObject.customersAndProjectsSelected?.size);
  const matchCustomerIds = new Set<string>();
  const matchProjectIds = new Set<string>();
  const qcsIdForClientOnlyOption = '0';
  filterObject.customersAndProjectsSelected?.forEach((item: CustomerAndProjects) => {
    const customerId = item?.customer?.id;
    const projectIdArray = item?.projects?.map((project) => project?.id);
    if (projectIdArray && Array.isArray(projectIdArray)) {
      for (const projectId of projectIdArray) {
        if (projectId !== qcsIdForClientOnlyOption) {
          // Ensure that User had not selected 'Client only' option on filter UI for this customer.
          matchProjectIds.add(projectId || '');
        } else {
          // Case of 'Client only' option.
          // Get customerId since we need to compare transactions with this customerId.
          matchCustomerIds.add(customerId || '');
        }
      }
    }
  });

  if (
    isBillOrSubscriptionFilter == null &&
    isExcludedFromReportsFilter == null &&
    isReviewedFilter == null &&
    isExcludedFromF2SFilter == null &&
    !dateStartISO &&
    !dateEndISO &&
    !accountIds &&
    !payeeNames &&
    !businessIds &&
    !flagValues &&
    !categoryIds &&
    !tagIds &&
    !hasCustomersAndProjects
  ) {
    return () => accepted;
  }

  const hasCategory = (
    coa: ChartOfAccountProps | null | undefined,
    ids: Set<string>,
    matchBalanceAdjustment: boolean,
  ) =>
    ids.has(coa?.id ?? '') ||
    (coa?.type === 'UNCATEGORIZED' && ids.has('UNCATEGORIZED')) ||
    (matchBalanceAdjustment && coa?.type === 'BALANCE_ADJUSTMENT' && ids.has(`BALANCE_ADJUSTMENT_${coa?.id}`));

  return (txn) => {
    const isTxnItem = 'item' in txn;
    const representsSplitItem = txnItemRepresentsSplitItem(txn);
    const transaction: TransactionProps | TransactionItemCombinedProps = isTxnItem ? txn.item : txn; // Normalize access
    const { postedOn } = transaction;

    if (postedOn) {
      if (dateStartISO && !(postedOn >= dateStartISO)) return rejected;
      if (dateEndISO && !(postedOn <= dateEndISO)) return rejected;
    }
    if (accountIds && !accountIds.includes(transaction.accountId ?? '')) return rejected;
    if (isBillOrSubscriptionFilter != null && isBillOrSubscription(transaction) !== isBillOrSubscriptionFilter) {
      return rejected;
    }
    if (
      isExcludedFromReportsFilter != null &&
      Boolean(transaction.isExcludedFromReports) !== isExcludedFromReportsFilter
    ) {
      return rejected;
    }
    if (isReviewedFilter != null && Boolean(transaction.isReviewed) !== isReviewedFilter) return rejected;
    if (isExcludedFromF2SFilter != null && Boolean(transaction.isExcludedFromF2S) !== isExcludedFromF2SFilter) {
      return rejected;
    }
    if (payeeNames && !payeeNames.has(transaction.payee?.toLowerCase() ?? '')) return rejected;
    if (businessIds) {
      const businessId = transaction.businessId ?? '';
      const isPersonal = businessId === '' || businessId === '0';
      if (isPersonal ? !wantsPersonal : !businessIds.has(businessId)) return rejected;
    }
    if (flagValues) {
      if (wantsNoFlag ? transaction.userFlag !== null : !flagValues.includes(transaction.userFlag ?? '')) {
        return rejected;
      }
    }
    // Same rules as txnHasCoa and txnHasTag: split parents match through their items.
    const matchesThroughSplitItems = !representsSplitItem && isSplitTxn(transaction);
    if (categoryIds) {
      const matches = matchesThroughSplitItems
        ? transaction.split?.items?.some((item) => hasCategory(item.coa, categoryIds, false))
        : hasCategory(transaction.coa, categoryIds, true);
      if (!matches) return rejected;
    }
    if (tagIds) {
      const matches = matchesThroughSplitItems
        ? transaction.split?.items?.some((item) => Boolean(item.tags?.some((tag) => tagIds.has(tag.id ?? ''))))
        : transaction.tags?.some((tag) => tagIds.has(tag.id ?? ''));
      if (!matches) return rejected;
    }

    if (hasCustomersAndProjects) {
      // If transaction has projectId, check for projectId match first
      if (transaction.split && !isTxnItem && transaction?.split?.items) {
        // dont want to add a txn item if the base txn has a split
        const { splitCustomerIds, splitProjectIds } = (
          transaction.split.items as ImmutableList<Immutable.RecordOf<SplitItemProps>>
        ) // splits can also be typed SplitItemProps[] so ts complains but they both work the same
          .reduce(
            (acc, item) => {
              if (item.customerId) acc.splitCustomerIds.add(item.customerId);
              if (item.projectId) acc.splitProjectIds.add(item.projectId);
              return acc;
            },
            { splitCustomerIds: new Set<string>(), splitProjectIds: new Set<string>() },
          );
        // This branch returns before the filterOut inversion.
        if (splitProjectIds.size) {
          return [...splitProjectIds].some((id) => matchProjectIds.has(id));
        } else {
          return [...splitCustomerIds].some((id) => matchCustomerIds.has(id));
        }
      }
      const matches = transaction.projectId
        ? matchProjectIds.has(transaction.projectId || '')
        : // Check for customerId match
          matchCustomerIds.has(transaction.customerId || '');
      if (!matches) return rejected;
    }

    return accepted;
  };
};

export const doesTxnMatchFilterObj = (
  txn: FilterableTransaction,
  filterObject?: filtersTypes.FilterObject | null,
): boolean => createTransactionsFilterByObjFn(filterObject)(txn);

export const filterTransactionsByFilterObject = (
  txns: Array<TransactionProps> | Collection<string | number, TransactionProps>,
  filterObject: filtersTypes.FilterObject | null | undefined,
) =>
  Array.isArray(txns)
    ? txns.filter(createTransactionsFilterByObjFn(filterObject))
    : txns.filter(createTransactionsFilterByObjFn(filterObject));

export const SortModes = Object.freeze({
  PENDING_TO_CLEARED: 'PENDING_TO_CLEARED',
  DATE_OLD_TO_NEW: 'DATE_OLD_TO_NEW',
  DATE_NEW_TO_OLD: 'DATE_NEW_TO_OLD',
  AMOUNT_HIGH_TO_LOW: 'AMOUNT_HIGH_TO_LOW',
  AMOUNT_LOW_TO_HIGH: 'AMOUNT_LOW_TO_HIGH',
  BILLER_NAME: 'BILLER_NAME',
});

type TransactionSortFields = Pick<TransactionProps, 'state' | 'postedOn' | 'payee' | 'amount'>;
export type TransactionSortMode = (typeof SortModes)[keyof typeof SortModes];

const subTransactionsCompare = (transaction1: TransactionSortFields, transaction2: TransactionSortFields) =>
  strCompare(transaction2.postedOn, transaction1.postedOn) ||
  numericCollator.compare(transaction2.payee ?? '', transaction1.payee ?? '');
export const createTransactionsCompareByModeFn =
  (sortMode: TransactionSortMode = SortModes.PENDING_TO_CLEARED) =>
  (transaction1: TransactionSortFields, transaction2: TransactionSortFields) => {
    switch (sortMode) {
      case SortModes.BILLER_NAME:
        return (
          numericCollator.compare(transaction1.payee ?? '', transaction2.payee ?? '') ||
          subTransactionsCompare(transaction1, transaction2)
        );
      case SortModes.PENDING_TO_CLEARED:
        return (
          (transaction1.state === 'PENDING' ? 0 : 1) - (transaction2.state === 'PENDING' ? 0 : 1) ||
          subTransactionsCompare(transaction1, transaction2)
        );
      case SortModes.DATE_OLD_TO_NEW:
        return strCompare(transaction1.postedOn, transaction2.postedOn);
      case SortModes.DATE_NEW_TO_OLD:
        return strCompare(transaction2.postedOn, transaction1.postedOn);
      case SortModes.AMOUNT_HIGH_TO_LOW:
        return (
          (transaction1.amount ?? 0) - (transaction2.amount ?? 0) || subTransactionsCompare(transaction1, transaction2)
        );
      case SortModes.AMOUNT_LOW_TO_HIGH:
        return (
          (transaction2.amount ?? 0) - (transaction1.amount ?? 0) || subTransactionsCompare(transaction1, transaction2)
        );
      default:
        assert(false, `unexpected sortMode = ${sortMode}`);
        return 0;
    }
  };

/**
 * Sorts in place like `Array.prototype.sort` with createTransactionsCompareByModeFn, but reads each
 * transaction's sort fields once instead of on every comparison.
 */
export function sortTransactionsByMode<T extends TransactionSortFields>(
  txns: T[],
  sortMode?: TransactionSortMode,
): T[] {
  if (txns.length < 2) return txns;
  const keyed = txns.map((txn) => ({
    txn,
    state: txn.state,
    postedOn: txn.postedOn,
    payee: txn.payee,
    amount: txn.amount,
  }));
  keyed.sort(createTransactionsCompareByModeFn(sortMode));
  keyed.forEach((entry, i) => {
    txns[i] = entry.txn;
  });
  return txns;
}

export const isBillOrSubscription = (txn: TransactionProps | TransactionItemCombinedProps | null | undefined) =>
  txn?.isBill || txn?.isSubscription || false;

// # ------------------------------------------------------------------------- #
// #                             Pre-Process Utils                             #
// # ------------------------------------------------------------------------- #

// -------------------------------------------------------
export function setReportsFlags(txn: TransactionRecord, value: any) {
  return txn && txn.set('isExcludedFromReports', Boolean(value)).set('isExcludedFromF2S', Boolean(value));
}

type IncomeCoaLookup = Immutable.Set<string> | null | undefined;
type BusinessCoaLookup = Immutable.Set<string> | null | undefined;

export function isAutoExcludedFromF2S(
  coa: { id?: string; type?: ChartOfAccountProps['type'] } | null | undefined,
  excludeIncomeByDefault: boolean,
  incomeCoaIds?: IncomeCoaLookup,
  businessCoaIds?: BusinessCoaLookup,
): boolean {
  if (!coa) return false;
  const isTransfer = coa?.type ? chartOfAccountsUtils.isTransferCOA(coa as ChartOfAccountProps) : false;
  const coaId = coa.id ?? '';
  const isExcludedIncome = excludeIncomeByDefault && Boolean(incomeCoaIds?.has?.(coaId));
  const isBusinessCategory = coa.type === 'CATEGORY' && Boolean(businessCoaIds?.has?.(coaId));
  return isTransfer || isExcludedIncome || isBusinessCategory;
}

function currenciesAreValidAndDifferent(a: any, b: any) {
  if (a === 'N/A' || b === 'N/A') {
    return false;
  }
  return a !== b;
}
// -------------------------------------------------------
export function isMultiCurrencyTransferTxn(txn: TransactionProps, state?: RootState | null) {
  let isMultiCurrency = false;
  const txnCurrency = accountsUtils.getCurrencyString(txn.accountId, state);

  if (isTransferTxn(txn)) {
    if (isSplitTxn(txn)) {
      txn.split?.items?.forEach((item) => {
        if (item.coa?.type === 'ACCOUNT') {
          isMultiCurrency =
            isMultiCurrency ||
            currenciesAreValidAndDifferent(accountsUtils.getCurrencyString(item.coa.id, state), txnCurrency);
        }
      });
    } else {
      isMultiCurrency = currenciesAreValidAndDifferent(
        accountsUtils.getCurrencyString(txn.coa?.id, state),
        txnCurrency,
      );
    }
  }
  return isMultiCurrency;
}

// -------------------------------------------------------
export function isUnsyncedTransferTxn(txn: TransactionProps, state?: RootState) {
  if (isSplitTxn(txn)) {
    return Boolean(txn.split?.items?.find((item) => chartOfAccountsUtils.isUnsyncedTransferCoa(item.coa, state)));
  }
  return chartOfAccountsUtils.isUnsyncedTransferCoa(txn.coa, state);
}

// -------------------------------------------------------
// TODO: change the feature flags type when the flux/feature-flags module is converted to TS
export function txnHasUnsupportedTransfer(
  txn: Partial<TransactionProps> | Partial<SplitItemProps>,
  featureFlags: ImmutableMap<string, boolean>,
  state: Pick<RootState, accountsTypes.ACCOUNTS_REDUCER_KEY | typeof FEATURE_FLAGS_REDUCER_KEY>,
) {
  if (!isTransferTxn(txn)) return false;

  const hideConnectedLoanTransactions = featureFlags.get('hideConnectedLoanTransactions');

  if (txn && 'split' in txn && isSplitTxn(txn)) {
    return Boolean(
      txn.split?.items?.find(
        (item) =>
          item.coa?.type === 'ACCOUNT' &&
          accountsUtils.transactionsNotSupportedForAccountId(item.coa.id, hideConnectedLoanTransactions, state),
      ),
    );
  }
  return accountsUtils.transactionsNotSupportedForAccountId(txn.coa?.id, hideConnectedLoanTransactions, state);
}
// -------------------------------------------------------
export function isSupportedTransferTxnWithFlags(
  txn: TransactionProps | SplitItemProps,
  featureFlags: ImmutableMap<string, boolean>,
  state: Pick<RootState, ACCOUNTS_REDUCER_KEY | typeof FEATURE_FLAGS_REDUCER_KEY>,
) {
  if (!isTransferTxn(txn)) return false;
  return !txnHasUnsupportedTransfer(txn, featureFlags, state);
}

export function isPendingTxn(txn: TransactionProps | null) {
  return txn && txn.state === 'PENDING';
}

// When entering a new transfer transaction, looks for an already existing transaction that may
// be a possible match for user prompting
export function addPossibleMatch(
  match: TransactionRecord,
  baseTxn: TransactionRecord,
  allMatches: Array<{
    possibleMatch: TransactionRecord;
    baseTxn: TransactionRecord;
    forceWarn: boolean;
  }>,
  forceWarn = false,
) {
  const possibleMatch = match.merge({
    coa: { id: baseTxn.accountId ?? '', type: 'ACCOUNT' },
    isExcludedFromF2S: true,
    isExcludedFromReports: true,
    transfer: { id: baseTxn?.id, clientId: baseTxn?.clientId },
  });
  const updatedBase = baseTxn.set('transfer', { clientId: possibleMatch?.clientId, id: possibleMatch?.id });
  allMatches.push({ possibleMatch, baseTxn: updatedBase, forceWarn });
}

//-------------------------------------------------------
export function findTransaction(
  txnsByAccountId: ImmutableMap<string, ImmutableMap<string, TransactionRecord>>,
  txnId: string,
  accountId?: string,
) {
  if (accountId) {
    const acctTxns = txnsByAccountId?.get(accountId);
    if (acctTxns) {
      const foundTxn = acctTxns.get(txnId);
      if (foundTxn) return foundTxn;
    }
  }

  // slow way
  let ret: TransactionRecord | null = null;
  txnsByAccountId.some((txnList) => {
    const foundTxn = txnList.get(txnId);
    if (foundTxn) {
      ret = foundTxn;
      return true;
    }
    return false;
  });
  return ret;
}

//-------------------------------------------------------
export function sortTxnsByDateDifference(txns: ImmutableList<TransactionRecord>, referenceDate: string) {
  const referenceDateTime = DateTime.fromISO(referenceDate);
  return txns.sort((a, b) => {
    const diff1 = Math.abs(DateTime.fromISO(a.postedOn).diff(referenceDateTime, 'days').days);
    const diff2 = Math.abs(DateTime.fromISO(b.postedOn).diff(referenceDateTime, 'days').days);
    if (diff1 === diff2) {
      return 0;
    }
    if (diff1 > diff2) {
      return 1;
    }
    return -1;
  });
}

// The code below is used to find possible transfers for new transactions being inserted.  When a new
// transfer transaction is entered, we look in the transfer account for a possible transaction match that
// may already be there, and offer the user to match to that transaction rather than create a dup
//
function isPossibleMatch(txn: TransactionProps, postedOn: string, amount: number, days = 31) {
  const diff = DateTime.fromISO(postedOn).diff(DateTime.fromISO(txn.postedOn), 'days').days;
  return !isSplitTxn(txn) && !isTransferTxn(txn) && Math.abs(diff) <= days && Number(amount) === Number(txn.amount);
}
//-------------------------------------------------------
export function findPossibleTransferCandidate(
  accountId: string,
  postedOn: string,
  amount: number,
  txnsByAccountId: ImmutableMap<string, ImmutableMap<string, TransactionRecord>>,
  days?: number,
) {
  const acctTxns = txnsByAccountId.get(accountId) || ImmutableMap();
  const txns = acctTxns.filter((tx) => isPossibleMatch(tx, postedOn, amount, days)).toList();
  if (txns) {
    return sortTxnsByDateDifference(txns, postedOn).first();
  }
  return null;
}
//-------------------------------------------------------
export function possiblyMatchTransfer(
  _txn: TransactionRecord,
  txnsByAccountId: ImmutableMap<string, ImmutableMap<string, TransactionRecord>>,
) {
  let txn = _txn;
  if (!isSplitTxn(txn) && !isTransferTxn(txn) && chartOfAccountsUtils.isL1TransferCoa(txn.coa)) {
    let matches = ImmutableList<TransactionRecord>();
    txnsByAccountId.forEach((_acctTxns, acctId) => {
      if (acctId !== txn.accountId) {
        // check if there is a matching transaction and score it
        const match = findPossibleTransferCandidate(acctId, txn.postedOn, -(txn.amount ?? 0), txnsByAccountId, 5);
        if (match) {
          matches = matches.push(match);
        }
      }
    });
    if (matches.size > 0) {
      const bestMatch = sortTxnsByDateDifference(matches, txn.postedOn).first();
      txn = txn.set('coa', { type: 'ACCOUNT', id: bestMatch?.accountId ?? '' });
    }
  }
  return txn;
}

//-------------------------------------------------------
export function coasAreDifferent(
  coa1: chartOfAccountsTypes.ChartOfAccountProps | null | undefined,
  coa2: chartOfAccountsTypes.ChartOfAccountProps | null | undefined,
) {
  if (!coa1 && !coa2) return false;
  if (!coa1 || !coa2) return true;
  return coa1.type !== coa2.type || coa1.id !== coa2.id;
}

//-------------------------------------------------------
export function areTagsEqual(
  tags1: Array<tagsTypes.TagProps> | ImmutableList<tagsTypes.TagProps> | null | undefined,
  tags2: Array<tagsTypes.TagProps> | ImmutableList<tagsTypes.TagProps> | null | undefined,
) {
  if (
    (!tags1 || ('length' in tags1 && tags1.length === 0) || ('size' in tags1 && tags1.size === 0)) &&
    (!tags2 || ('length' in tags2 && tags2.length === 0) || ('size' in tags2 && tags2.size === 0))
  ) {
    // both either null or empty
    return true;
  }
  if (!tags1 || !tags2) return false;
  const sortCallback = (a: tagsTypes.TagProps, b: tagsTypes.TagProps) => (getId(a) > getId(b) ? 1 : -1);
  const reduceCallback = (ids: string, tag: tagsTypes.TagProps) => `${ids}-${getId(tag)}`;

  // technically the reduce methods on arrays and immutable lists are not compatible, but we are only using the common arguments so we do this type dance to make the compiler happy
  const sum1 = Array.isArray(tags1)
    ? tags1.sort(sortCallback).reduce(reduceCallback, '')
    : tags1.sort(sortCallback).reduce(reduceCallback, '');

  const sum2 = Array.isArray(tags2)
    ? tags2.sort(sortCallback).reduce(reduceCallback, '')
    : tags2.sort(sortCallback).reduce(reduceCallback, '');

  return sum1 === sum2;
}

function splitStringVal(item: SplitItemProps) {
  return `${item.coa?.id}-${Number(item.amount).toFixed(2)}-${item.name}-${item.memo}-${
    item.tags ? (Array.isArray(item.tags) ? item.tags.length : item.tags.size) : 'none'
  }`;
}
//-------------------------------------------------------
export function fieldChanged(txn: TransactionProps, txnPrev: TransactionProps, field: keyof TransactionProps) {
  // this is checking if one or the other is null, however, we have to do a bit more
  // for splits since even though .split may not be null, it can still be a non-split if
  // there are no items

  // should not be called with either value being null
  if (!txn || !txnPrev) return false;

  if (!txn[field] || !txnPrev[field]) {
    return field === 'split'
      ? isSplitTxn(txn) !== isSplitTxn(txnPrev)
      : Boolean(txn[field]) !== Boolean(txnPrev[field]);
  }
  switch (field) {
    case 'amount': {
      return Number(txnPrev.amount) !== Number(txn.amount);
    }

    case 'postedOn': {
      return txnPrev.postedOn !== txn.postedOn;
    }

    case 'coa': {
      return coasAreDifferent(txnPrev.coa, txn.coa); // ((txnPrev.coa.id !== txn.coa.id) || (txnPrev.coa.type !== txn.coa.type));
    }

    case 'check': {
      return txnPrev.check?.number !== txn.check?.number;
    }

    case 'attachments': {
      const sum1 = txn[field]?.sort((a, b) => (a > b ? 1 : -1)).reduce((total, num) => `${total}-${num}`, '');
      const sum2 = txnPrev[field]?.sort((a, b) => (a > b ? 1 : -1)).reduce((total, num) => `${total}-${num}`, '');
      return sum1 !== sum2;
    }

    case 'tags': {
      return !areTagsEqual(txn[field], txnPrev[field]);
    }

    case 'split': {
      if (isSplitTxn(txn) !== isSplitTxn(txnPrev)) {
        return true;
      }
      const sortCb = (a: SplitItemProps, b: SplitItemProps) => ((a.coa?.id ?? '') > (b.coa?.id ?? '') ? 1 : -1);
      const reduceCb = (total: string, item: SplitItemProps) => `${total}-${splitStringVal(item)}`;

      const sorted1 = txnPrev.split?.items?.sort(sortCb);
      let sum1: string | undefined;
      // Technically the reduce function on an immutable list has more args than the
      // reduce function on an array, so we have to explicitly do this if/else to make TS happy.
      // Our callback is only using the first few common args though so it seems a little pointless.
      if (sorted1 && Array.isArray(sorted1)) {
        sum1 = sorted1.reduce(reduceCb, '');
      } else if (sorted1) {
        sum1 = sorted1.reduce(reduceCb, '');
      }

      const sorted2 = txn.split?.items?.sort(sortCb);
      let sum2: string | undefined;
      if (sorted2 && Array.isArray(sorted2)) {
        sum2 = sorted2.reduce(reduceCb, '');
      } else if (sorted2) {
        sum2 = sorted2.reduce(reduceCb, '');
      }

      return sum1 !== sum2;
    }
    default:
      if (txnPrev) {
        return txn[field] !== txnPrev[field];
      }
  }
  return false;
}

const noNaN = (n: string) => (Number.parseFloat(n) ? n : 0);
// -------------------------------------------------------
// takes an immutable transaction, and checks for tags with ID="0" and tries to
// find a tag that is suitable with a real ID.  Returns an immutable transaction
// with updates if they were needed
export function updateNewTags(txn: TransactionRecord, dropPlaceholders = false, state = store?.getState()) {
  let retTxn = txn;
  if (txn.tags) {
    const visited: tagsTypes.TagProps[] = [];
    const filterTag = (tag: tagsTypes.TagProps) => {
      // remove dupes or empty entries
      if (tag) {
        const alreadyHas = visited.find((x) => x.id === tag.id && (x.id !== '0' || x.name === tag.name));
        if (!alreadyHas) {
          visited.push(tag);
          return true;
        }
      }
      return false;
    };
    const mapTag = (tag: tagsTypes.TagProps) => {
      // find current tag
      if (noNaN(tag.id ?? '') === 0) {
        const ret = state ? tagsSelectors.getTagByName(state, tag.name ?? '') : null;
        return ret && ret.id ? ret : (tag as tagsTypes.Tag);
      }
      return tag as tagsTypes.Tag;
    };
    // Technicaly arrays and immutable lists have different signature for their filter and map methods
    // We are only using the first arg which is the same, but we still have to typecheck to make the compiler happy
    const newTags = Array.isArray(txn.tags)
      ? txn.tags.filter(filterTag).map(mapTag)
      : txn.tags.filter(filterTag).map(mapTag);

    const filterEmpty = (x: tagsTypes.TagProps) => x !== null && (!dropPlaceholders || noNaN(x?.id ?? '') !== 0);
    retTxn = txn.set(
      'tags',
      Array.isArray(newTags)
        ? ImmutableList(newTags.filter(filterEmpty).map((t) => tagsTypes.mkTag(t)))
        : newTags.filter(filterEmpty),
    );
  }
  if (isSplitTxn(retTxn)) {
    const newItems = retTxn.split?.items?.map((item) => {
      if (item?.tags) {
        const newSplitTags = item.tags.map((tag) => {
          if (noNaN(tag.id ?? '') === 0) {
            const ret = store ? tagsSelectors.getTagByName(store.getState(), tag.name ?? '') : null;
            if (ret && ret.id) {
              return ret;
            }
            return dropPlaceholders ? null : tag;
          }
          return tag;
        });
        const filterCb = (x: tagsTypes.TagProps | null) => x !== null && (!dropPlaceholders || noNaN(x.id ?? '') !== 0);
        const filteredTags = Array.isArray(newSplitTags)
          ? (newSplitTags.filter(filterCb) as tagsTypes.Tag[])
          : (newSplitTags.filter(filterCb) as ImmutableList<tagsTypes.Tag>);
        if ('set' in item) {
          // immutable record
          return item?.set('tags', filteredTags) || item;
        }
        const newItem = item;
        newItem.tags = filteredTags;
        return newItem;
      }
      return item;
    });

    retTxn = retTxn.setIn(['split', 'items'], newItems);
  }
  return retTxn;
}

export function isExistingTxn(txn: TransactionProps | null | undefined) {
  return txn?.id && txn.id !== '0';
}

// simple protected dateTime check
export function dateTimeFromTxn(txn: TransactionProps) {
  return (
    (txn.postedOn && DateTime.fromISO(txn.postedOn)) ||
    (txn.stDueOn && DateTime.fromISO(txn.stDueOn)) ||
    DateTime.local()
  ).startOf('day');
}

const protectedLookup = (split: SplitProps | null | undefined, i: number) => {
  if (!split || !split.items) {
    return null;
  }
  return 'get' in split.items ? split?.items.get(i) : split?.items?.[i];
};

export function formatCategoryDisplayString(
  txn: TransactionProps,
  categoriesById: ReturnType<typeof categoriesSelectors.getCategoriesById>,
  accountsById: ReturnType<typeof accountsSelectors.getAccountsById>,
  noParent = true,
): string {
  const isSplit = isSplitTxn(txn);
  if (isSplit) {
    const { split } = txn;
    const firstItem = protectedLookup(split, 0);
    const secondItem = protectedLookup(split, 1);
    const thirdItem = protectedLookup(split, 2);
    return `${chartOfAccountsUtils.getCoaString(
      firstItem?.coa,
      categoriesById,
      accountsById,
      true,
    )}, ${chartOfAccountsUtils.getCoaString(secondItem?.coa, categoriesById, accountsById, true)}${thirdItem ? ', ...' : ''}`;
  }
  return chartOfAccountsUtils.getCoaString(txn.coa, categoriesById, accountsById, noParent);
}

const MISC: string[] = [InvestmentTxnType.EXPENSE_MISCELLANEOUS, InvestmentTxnType.INCOME_MISCELLANEOUS];
export const isExposedTransaction = (
  transaction: TransactionProps,
  accounts: ImmutableMap<string, Pick<accountsTypes.Account, 'type'>>,
  exposeInvestmentTransactions: string,
) =>
  accounts.get(transaction.accountId ?? '')?.type !== 'INVESTMENT' ||
  exposeInvestmentTransactions === 'ALL' ||
  (exposeInvestmentTransactions === 'CASH_FLOW+MISC' &&
    (transaction.type === 'CASH_FLOW' || MISC.includes(transaction.investmentTxnType ?? ''))) ||
  (exposeInvestmentTransactions === 'CASH_FLOW' && transaction.type === 'CASH_FLOW') ||
  (exposeInvestmentTransactions === 'HIDE' && false);

export const isConnectedLoanTransaction = (
  txn: TransactionProps,
  accounts: ImmutableMap<string, Pick<accountsTypes.Account, 'type' | 'institutionLoginId'>>,
) => {
  const account = accounts.get(txn.accountId ?? '');
  if (account?.type === 'LOAN' && !accountsUtils.isAccountManual(account)) {
    return true;
  }
  return false;
};

export function getFieldString(field: string, txn: TransactionProps, longCats?: boolean) {
  switch (field) {
    case 'postedOn':
    case 'date':
      return DateTime.fromISO(txn.postedOn).toLocaleString(DateTime.DATE_SHORT);
    case 'category':
      // currently displays 'split' for splits, if you want listed split items than you can use 'formatCategoryDisplayString' above
      return isSplitTxn(txn) ? 'SPLIT' : chartOfAccountsSelectors.getCoaStringSelector(undefined, txn.coa, longCats);
    case 'account':
    case 'accountColor':
      return accountsUtils.getAccountString(txn.accountId);
    case 'amount':
      return numberUtils.formatNumber(txn.amount, accountsUtils.getCurrencyString(txn.accountId), '0,00.00');
    case 'balance':
      return numberUtils.formatNumber(txn.balance || 0, accountsUtils.getCurrencyString(txn.accountId), '0,00.00');
    case 'tags': {
      return makeTagsString(txn.tags) || '';
    }
    case 'check': {
      return txn.check && txn.check.number;
    }
    case 'status':
      return txn.state;
    case 'notes':
      return txn.memo ? txn.memo : '';
    default:
      return `${field}` in txn ? (txn as any)[field] : '';
  }
}

export const isNonBilledBillableItem = (txnItem: TransactionItem) =>
  Boolean(txnItem.item.isBillable && !txnItem.item.linkedItem?.id);

export const isNonBilledItem = (txnItem: TransactionItem) => Boolean(!txnItem.item.linkedItem?.id);

// the reduce from allocations can be either be an array or a list
export const sumAllocations = (txn: TransactionProps | TransactionRecord) => {
  let paid = 0;
  if (!txn.allocations) {
    return paid;
  }
  if (Array.isArray(txn.allocations)) {
    paid = txn.allocations.reduce<number>((s: number, allocation: AllocationProps) => s + (allocation.amount ?? 0), 0);
  } else if (ImmutableList.isList(txn.allocations)) {
    paid = txn.allocations.reduce<number>((s: number, allocation: Allocation) => s + (allocation.amount ?? 0), 0);
  }
  return numberUtils.floatMathAdjuster(paid);
};

export const calculateBillablePlusTxnSum = (
  billableItems: BillableInvoiceItemType[],
  unbilledTransactions: TransactionItem[],
): number => {
  const billableSum = billableItems.reduce((sum, item) => sum + (item.rate ?? 0) * (item.units ?? 0), 0);
  const transactionSum = unbilledTransactions.reduce((sum, txn) => sum + txn.item.amount, 0);

  return billableSum - transactionSum;
};

export const isCheckNumberVisibleOnInvestmentTxnType = (investmentTxnType: string | null | undefined) => {
  switch (investmentTxnType) {
    case 'VIRTUAL_CASH_FLOW':
      return true;
  }

  return false;
};

export const isCategoryVisibleOnInvestmentTxnType = (investmentTxnType: string | null | undefined) => {
  switch (investmentTxnType) {
    case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
    case InvestmentTxnType.INCOME_MISCELLANEOUS:
    case 'VIRTUAL_CASH_FLOW':
      return true;
  }

  return false;
};

export const createPlaceholderTxn = (
  payee: string,
  amount: number,
  amountOn: string,
  txnId: string,
  splitId?: string,
) => {
  let splitTxn: SplitItem | undefined;
  if (splitId) {
    splitTxn = mkSplitItem({ amount, id: splitId });
  }
  const txn = mkCashFlowTransaction({
    payee,
    amount,
    state: 'CLEARED',
    postedOn: amountOn,
    id: txnId,
    ...(splitTxn ? { split: { items: [splitTxn] } } : undefined),
  });
  return mkTransactionItem({
    transaction: txn,
    splitItem: splitTxn ? splitTxn : undefined,
    item: {
      ...txn.toObject(),
      ...splitTxn?.toObject(),
      isPartial: false,
      unpaidAmount: undefined,
      readOnly: true,
    },
  });
};
export const isTxnMatch = (a: TransactionProps, b: TransactionProps): boolean => {
  if ((a.id || b.id) && a.id === b.id) {
    return true;
  }
  if ((a.clientId || b.clientId) && a.clientId === b.clientId) {
    return true;
  }
  return false;
};
const merger = <T>(a: T, b: T): T => b ?? a;
export const mergeTxns = (a: TransactionRecord, b: TransactionRecord) => {
  if (a.state === undefined) {
    // a is a patch so it cannot be the base
    return b.mergeWith(merger, a);
  }
  return a.mergeWith(merger, b);
};

// This function is used to find a matching transfer transaction for a given transaction using the linkage field.
// The linkage field is likely reliable, but it didn't always exist and there has been defects. If a transfer is
// returned, then it is the correct one. If one isn't returned on a transfer txn, then either it doesn't exist (broken
// transfer) or the linkage field is incorrect. There is another method that can heuristically search for the correct
// transfer but it is expensive and could detect the wrong transfer if multiple transfers exist that are similar.

export const findMatchingTransferTxn = (
  txnsbyId: ImmutableMap<string, TransactionRecord>,
  txn: Partial<TransactionProps>,
  splitItem?: Partial<SplitItemProps>,
): { txn: TransactionRecord; splitItem: number | null } | undefined => {
  const txAmount = splitItem?.amount || txn.amount;
  const txTransfer = splitItem?.transfer || txn.transfer;

  let maybeTxn;
  if (txTransfer?.id) {
    maybeTxn = txnsbyId.get(txTransfer.id);
  }
  if (maybeTxn) {
    if (!isSplitTxn(maybeTxn)) {
      // check that transfer matches
      let transferMatches;
      if (txn?.id) {
        transferMatches = maybeTxn.transfer?.id === txn.id;
      }
      // verify still connected via COA and amount (COA should always be same since user is passing in the txnList)
      if (transferMatches && maybeTxn.coa?.id === txn.accountId && Number(maybeTxn.amount) === -Number(txAmount)) {
        return { txn: maybeTxn, splitItem: null };
      }
    } else {
      // find matching split-item
      const items = maybeTxn.split?.items ?? [];
      for (let i = 0; i < (isImmutable(items) ? items.size : items.length); i++) {
        const splitItem = isImmutable(items) ? items.get(i) : items[i];
        if (
          splitItem?.transfer?.id === txn.id &&
          splitItem?.coa?.id === txn.accountId &&
          Number(splitItem?.amount) === -Number(txAmount)
        ) {
          return { txn: maybeTxn, splitItem: i };
        }
      }
    }
  }
  return undefined;
};

export const isDebitTxn = (txn: TransactionProps) => txn.amount > -0.005;

export const canBePaymentToAccountsReceivable = (
  txn: TransactionProps,
  account: accountsTypes.Account | undefined,
  incomeCoaIds: Immutable.Set<string>,
  matchingTransferIfTransfer?: { txn: TransactionRecord; splitItem: number | null },
) => {
  const isBizIncome = incomeCoaIds?.includes(txn.coa?.id ?? '') && txn.businessId;
  const isUncategorized = txn.coa?.type === 'UNCATEGORIZED';

  return (
    accountsUtils.doesAccountSupportPaymentSrcs(account) &&
    !isSplitTxn(txn) &&
    !(matchingTransferIfTransfer && isSplitTxn(matchingTransferIfTransfer.txn)) &&
    isDebitTxn(txn) &&
    (isBizIncome || isUncategorized)
  );
};

export const canBeRefundFromAccountsReceivable = (
  txn: TransactionProps,
  account: accountsTypes.Account | undefined,
  matchingTransferIfTransfer?: { txn: TransactionRecord; splitItem: number | null },
) => {
  return (
    account &&
    accountsUtils.doesAccountSupportRefundSrcs(account) &&
    !isSplitTxn(txn) &&
    !(matchingTransferIfTransfer && isSplitTxn(matchingTransferIfTransfer.txn)) &&
    !isDebitTxn(txn)
  );
};

export const isTxnInAccountsReceivable = (
  txn: TransactionProps,
  accountsById: ImmutableMap<string, accountsTypes.Account>,
) => {
  const account = accountsById.get(txn.accountId as string);
  return account && account.type === 'ACCOUNTS_RECEIVABLE';
};

export const isProtectedTxn = (
  txn: TransactionProps,
  accountsById: ImmutableMap<string, accountsTypes.Account>,
  matchingTransferIfTransfer?: { txn: TransactionRecord; splitItem: number | null },
) =>
  !!txn.linkedItem?.id ||
  !!txn.split?.items?.some((split) => !!split.linkedItem?.id) ||
  (matchingTransferIfTransfer && isSplitTxn(matchingTransferIfTransfer.txn)) ||
  isTxnInAccountsReceivable(txn, accountsById);

export const isOtherSideInAccountsReceivable = (
  accountsById: ImmutableMap<string, accountsTypes.Account>,
  matchingTransferIfTransfer?: { txn: TransactionRecord; splitItem: number | null },
) => {
  if (matchingTransferIfTransfer) {
    const otherSide = matchingTransferIfTransfer.txn;
    return isTxnInAccountsReceivable(otherSide, accountsById);
  }
  return false;
};

interface UnpaidAmounts {
  sent: number;
  draft: number;
}
export const sumUnpaidAmounts = (invoices: TransactionRecord[]): UnpaidAmounts =>
  reduce(invoices, { draft: 0, sent: 0 }, (sums: UnpaidAmounts, invoice: TransactionRecord) => {
    const unpaidAmount =
      (invoice.amount ?? 0) - reduce(invoice.allocations ?? [], 0, (sum, alloc) => (alloc.amount ?? 0) + sum);
    if (unpaidAmount > 0.005) {
      if (invoice.sentOn) {
        return {
          ...sums,
          sent: sums.sent + unpaidAmount,
        };
      }
      return {
        ...sums,
        draft: sums.draft + unpaidAmount,
      };
    }
    return sums;
  });

export const isEstimateTransaction = (transaction: TransactionProps) => transaction?.subtype === 'ESTIMATE';

export const convertEstimateToInvoiceTransaction = (
  invoice: InvoiceTransaction | TransactionProps,
  salesTaxCoa: chartOfAccountsTypes.ChartOfAccountProps | null,
  overrides: (Partial<InvoiceTransaction> & Required<Pick<InvoiceTransaction, 'invoiceNo' | 'dueOn'>>) | null,
): InvoiceTransaction | null => {
  return mkInvoiceTransaction({
    ...(ImRecord.isRecord(invoice) ? invoice.toObject() : invoice),
    id: undefined,
    clientId: uuid(),
    split: invoice?.split
      ? {
          items: ImmutableList(
            invoice.split?.items?.map((item) =>
              mkSplitItem({
                ...(ImRecord.isRecord(item) ? item?.toObject() : item),
                coa: item?.type !== 'INVOICE_TAX' ? item?.coa : salesTaxCoa,
                clientId: uuid(),
                id: null,
              }),
            ),
          ),
        }
      : null,
    subtype: TransactionSubType.Invoice,
    allocations: ImmutableList(),
    // Estimates do not own invoice due-date semantics; callers must set invoice terms/due date explicitly.
    dueOn: undefined,
    termsId: undefined,
    terms: undefined,
    ...overrides,
  });
};

export const invoiceSubtypeTitle = (subtype: TransactionSubType | null | undefined): string => {
  switch (subtype) {
    case TransactionSubType.InvoiceCreditMemo:
      return 'Credit Memo';
    case TransactionSubType.SalesReceipt:
      return 'Sales Receipt';
    case TransactionSubType.Estimate:
      return 'Estimate';
    case TransactionSubType.Invoice:
    default:
      return 'Invoice';
  }
};

export const getPaymentMethodTitle = (method: TxnPaymentMethodType | null | undefined): string => {
  switch (method) {
    case 'CASH':
      return 'Cash';
    case 'CHECK':
      return 'Check';
    case 'CREDIT_CARD':
      return 'Credit Card';
    case 'DEBIT_CARD':
      return 'Debit Card';
    case 'ONLINE_PAYMENT':
      return 'Online Payment';
    case 'OTHER':
      return 'Other';
    default:
      return '';
  }
};
